// src/lib/api-client.js
// 2026-05-29 EXTRACTED from src/App.jsx (C1 refactor — network-request layer) as a
// behavior-preserving TRANSFORM. The seven functions below were lifted VERBATIM
// (byte-identical bodies) out of the 6741-LOC App.jsx monolith.
//
// Why one file: these functions form a tightly-coupled call graph that crosses
// itself, so splitting them across modules would create import cycles:
//   - apiGet  <-> apiSave            (apiSave's data-loss guard calls apiGet to
//                                      recover; apiGet's retry path is the read side)
//   - apiSave  -> handleConflictResolution (on HTTP 409)
//   - connectWebSocket -> flushPendingSaves -> apiSave (reconnect drains the queue)
//   - flushPendingSaves / logPaymentAction are the queue-drain + telemetry beacons
//   - onWsUpdate registers WS fan-out listeners
// Keeping them together lets the mutual recursion resolve WITHIN this module.
//
// State contract: this module owns NONE of its state. Every mutable binding lives
// in a sibling singleton module (connection-state / network-state / data-versions /
// tombstones) extracted earlier in the C1 refactor. We import the live bindings
// (ESM reflects in-place updates and setter reassignments) — so there is exactly
// one shared instance of pendingSaves / dataVersions / conflictSetters / etc.
// across App.jsx, the page components, and this network layer.
//
// IMPORTANT — no App.jsx import: this file imports ONLY from the extracted sibling
// modules + version.js. It NEVER imports from App.jsx, so there is no cycle
// (App.jsx imports FROM here; api-client.js does not import back). App.jsx
// re-exports apiGet / apiSave / onWsUpdate / logPaymentAction from this module for
// backward compat (pages + App.test.jsx still `import { ... } from './App.jsx'`).
//
import { API_BASE, WS_URL } from './endpoints.js';
import { stampNow, elapsedSince } from './elapsed.js';
import { authHeaders } from './auth-headers.js';
import { confirmSessionExpired, isSessionKnownDead, checkLocalSessionExpiry, localSessionExpired } from './session-expiry.js';
import { getSessionToken, setSessionToken, getRawSession } from '../utils/session.js';
import { lsGet, lsSave, lsRemove } from './local-storage.js';
import {
  serverOnline,
  setServerOnline,
  pendingSaves,
  wsConnection,
  setWsConnection,
  wsReconnectTimer,
  setWsReconnectTimer,
  wsReconnectAttempts,
  setWsReconnectAttempts,
  resetStrikes,
} from './connection-state.js';
import {
  setSessionExpiredFlag,
  justLoggedIn,
  lastSaveTimestamps,
  mergeByIDGlobal,
  conflictSetters,
  lastKnownCounts,
  wsListeners,
  wsPresenceListeners,
} from './network-state.js';
import { createPresenceTracker } from './presence-tracker.js';
import { dataVersions, savePersistedVersions } from './data-versions.js';
import {
  deletedIDs,
  getAndClearDeletes,
  processTombstoned,
  // filterTombstoned больше не импортируется: все три вызова (apiGet, post-save sync,
  // WS onmessage) заменены на syncServerTombstones (2026-07-17 tombstone-контракт).
  syncServerTombstones,
  tombstonedIDs,
  // PR R (sub_R3, 28.09): снятие стале-меток удаления с daily-calcs-data строк, ставших
  // delete-protected — см. releaseProtectedDcMarks в tombstones.js и оба call site ниже.
  releaseProtectedDcMarks,
  untrackDelete,
} from './tombstones.js';
import { INITIAL_BUILD_HASH } from './build-version.js';
import { dispatchToast } from './toast-event.js';
import { coalescePatchRows, pruneDeliveredFields } from './row-patch.js';
import { VERSION } from '../version.js';
import {
  beginClockSample,
  endClockSample,
  noteHttpResponseSample,
  noteServerLowerBound,
  requestResync,
} from './server-clock.js';

/**
 * Pure decision function: should the client reload given a WS heartbeat message?
 *
 * Phase 1a: ONLY build-hash mismatch triggers reload. VERSION label mismatch is
 * intentionally ignored — build-hash is the authoritative signal for "new code
 * is deployed". This means bumping package.json version alone does NOT cause
 * every connected client to reload (they reload only on the next actual deploy
 * that changes the bundle). This is the desired behavior.
 *
 * Exported for unit tests. The heartbeat handler in connectWebSocket calls this.
 *
 * @param {object} msg - WS heartbeat message (must have .bh field)
 * @param {{version: string, buildHash: string}} ctx - current client state
 * @returns {boolean} true if a reload should be scheduled
 */
export function shouldReloadOnHeartbeat(msg, { buildHash }) {
  // Guard: only trigger when both sides have a valid hash to compare.
  // If either is absent (old server / first deploy without hash support)
  // we do NOT reload — fail-safe over fail-noisy.
  if (!msg.bh || !buildHash) return false;
  return msg.bh !== buildHash;
}

// 2026-08-18: вернуть сессию, стёртую по ложному 401. До этого ветка «ложная тревога»
// в App.jsx (v31.66) была практически недостижима: apiGet стирал токен ДО контрольного
// запроса, тот уходил без заголовка и получал честный 401 — то есть «подтверждал»
// смерть живой сессии. Снимок + возврат закрывают это: хоронит сессию только сервер,
// дважды ответивший 401 на её же токен.
function restoreKilledSession(token, sessionBlob) {
  if (!token) return;
  setSessionToken(token);
  if (sessionBlob) localStorage.setItem('blitz_session', sessionBlob);
  setSessionExpiredFlag(false);
  console.warn('♻️ SESSION_RESTORED — 401 был ложной тревогой, токен возвращён');
}

async function fetchAndTime(url, opts) {
  const started = beginClockSample();
  const res = await fetch(url, { ...opts, cache: 'no-store' });
  return { res, timing: endClockSample(started) };
}

// 2026-10-07 (Zack, медленный канал): сохранение, где все строки проиграли row-LWW, сервер
// больше не рассылает всем (crud-post.cjs: upd только для принятых строк) — раньше эта
// рассылка шла и отправителю и заодно заменяла его протухшие строки серверными. Теперь
// сервер отвечает unchanged + lwwStale (сколько отброшено с другим содержимым), и вкладка
// перечитывает ТОЛЬКО эту таблицу и прогоняет её тем же путём, что рассылку. Без этого
// протухшие строки оставались бы на экране, и правка любого их поля откатила бы чужие
// правки остальных полей (так 07.10 откатилась сделка 47 FR). Одно перечитывание на таблицу
// за раз — очередь неотправленных сохранений шлёт по нескольку таблиц подряд.
const staleResyncInFlight = new Set();
function resyncAfterStaleSave(endpoint, json) {
  if (!(Number(json?.lwwStale) > 0) || staleResyncInFlight.has(endpoint)) return;
  staleResyncInFlight.add(endpoint);
  apiGet(endpoint)
    .then((data) => {
      if (!Array.isArray(data)) return;
      // source 'poll' — снимок GET, как у опроса App.jsx (метка в логе слушателя).
      wsListeners.forEach((fn) => {
        try { fn({ type: 'update', table: endpoint, data, source: 'poll' }); } catch (e) { console.warn(`resync ${endpoint} listener failed:`, e?.message || e); }
      });
    })
    .finally(() => staleResyncInFlight.delete(endpoint));
}

export async function apiGet(endpoint) {
  try {
    const sentToken = getSessionToken();
    const { res, timing: getTiming } = await fetchAndTime(`${API_BASE}/${endpoint}`, { headers: authHeaders(), signal: AbortSignal.timeout(8000) }); // FIX: 8s total (not blocking init anymore)
    if (res.status === 401) {
      // v9.05: Retry twice before expiring session — prevents flicker-based logouts
      if (justLoggedIn) {
        console.log(`⚠️ 401 on ${endpoint} during login grace period — retrying...`);
        await new Promise(r => setTimeout(r, 500));
        const { res: retry, timing: retryTiming } = await fetchAndTime(`${API_BASE}/${endpoint}`, { headers: authHeaders(), signal: AbortSignal.timeout(6000) });
        if (retry.ok) {
          setServerOnline(true);
          const json = await retry.json();
          noteHttpResponseSample(retry, json, retryTiming);
          const data = json.data || json;
          if (json.version && json.version !== dataVersions[endpoint]) { dataVersions[endpoint] = json.version; savePersistedVersions(dataVersions); } // v11.02 L4
          if (Array.isArray(data)) lsSave(endpoint, data);
          return data;
        }
        return null;
      }
      // Not during login — retry once more before killing session (network flicker protection)
      try {
        await new Promise(r => setTimeout(r, 500));
        const { res: retry2, timing: retry2Timing } = await fetchAndTime(`${API_BASE}/${endpoint}`, { headers: authHeaders(), signal: AbortSignal.timeout(5000) });
        if (retry2.ok) {
          setServerOnline(true);
          const json = await retry2.json();
          noteHttpResponseSample(retry2, json, retry2Timing);
          const data = json.data || json;
          if (json.version) { dataVersions[endpoint] = json.version; savePersistedVersions(dataVersions); }
          if (Array.isArray(data)) lsSave(endpoint, data);
          return data;
        }
      } catch {}
      // Both retries failed — real session expiry
      // v31.66: diagnostic log для root-cause confirmation (per session_lifecycle audit).
      // Если "залогинен и выкинуло" повторится — этот log покажет точный context:
      // (1) endpoint который trigger'ил, (2) сколько часов session age, (3) token suffix
      // для correlation с backend audit log. Console DevTools screenshot быстро укажет path.
      // S3b: ответ на запрос старой сессии не трогает новую.
      if (getSessionToken() !== sentToken) return null;
      try {
        const _sess = JSON.parse(localStorage.getItem('blitz_session') || 'null');
        const _ageH = _sess?.loginTime ? ((Date.now() - _sess.loginTime) / 3600000).toFixed(2) : 'N/A';
        const _tok = sentToken || '';
        console.error(`🔴 SESSION_KILL @${endpoint}`, {
          justLoggedIn,
          sessionAgeHours: _ageH,
          tokenSuffix: _tok.slice(-8) || 'none',
          serverOnline,
          ts: new Date().toISOString(),
        });
      } catch {}
      // 2026-08-18: снимок сессии ДО стирания — по нему делается контрольный запрос
      // (модалка «Session expired») и по нему же сессия возвращается, если 401 окажется
      // ложной тревогой. После setSessionToken(null) ни того, ни другого уже не сделать.
      const killedToken = sentToken;
      const killedSession = localStorage.getItem('blitz_session');
      setSessionToken(null);
      localStorage.removeItem('blitz_session');
      setSessionExpiredFlag(true);
      confirmSessionExpired({
        token: killedToken,
        reason: `api-get:${endpoint}`,
        onAlive: () => restoreKilledSession(killedToken, killedSession),
      });
      return null;
    }
    // 2026-07-28 (аудит прав): 403 = таблица не положена этому сотруднику по pageAccess.
    // Это НЕ сбой сети и НЕ протухшая сессия — тихо отдаём null, страница отрисуется без
    // этих данных (загрузчик в App.jsx уже null-терпим по каждой таблице).
    // Через общий throw пускать нельзя: там health-check на каждый отказ, а App.jsx грузит
    // 15 таблиц разом — пачка 403 дала бы 15 лишних запросов и могла бы показать
    // «сервер офлайн» на ровном месте.
    if (res.status === 403) {
      // Бан по IP — это НЕ «таблица не положена». Данные человека остаются при нём:
      // ни кэш, ни склад спасённых правок не трогаем, таблицу запрещённой не метим.
      // Для приложения это просто недоступный сервер — так же, как обрыв сети.
      if (isIpBanResponse(res)) {
        console.warn(`⛔ apiGet ${endpoint}: доступ с этого адреса закрыт (бан по IP) — данные и очередь сохранены`);
        setServerOnline(false);
        return null;
      }
      forbiddenEndpoints.add(endpoint);
      lsRemove(endpoint);           // чужие данные не должны оставаться в кэше браузера
      purgeDroppedSaves(endpoint);  // ...и в аварийном хранилище тоже
      return null;
    }
    if (!res.ok) throw new Error('not ok');
    // Права могли вернуть — снимаем пометку, иначе таблица осталась бы запрещённой
    // до перезагрузки вкладки.
    forbiddenEndpoints.delete(endpoint);
    setServerOnline(true);
    // v31.66: reset stale sessionExpiredFlag после successful fetch. PRIOR: flag set
    // в apiGet (line ~1043) при 401 retry fail, но reset ТОЛЬКО в useEffect[user] line 11958.
    // Если fetch fired BEFORE useEffect's reset (race), flag оставался true → next
    // useEffect[user] cycle ловил stale true → ghost mass logout. Reset on every success
    // обеспечивает что flag отражает actual current state, не stale prior failure.
    setSessionExpiredFlag(false);
    const json = await res.json();
    noteHttpResponseSample(res, json, getTiming);
    let data = json.data || json;
    if (json.version && json.version !== dataVersions[endpoint]) { // v11.02 L4: only write when changed
          dataVersions[endpoint] = json.version;
          savePersistedVersions(dataVersions);
        }
    // PR R (sub_R3, 28.09): ДО syncServerTombstones/mergeByID — releaseProtectedDcMarks
    // снимает стале-метки старой вкладки со строк, ставших delete-protected (см. её
    // комментарий в tombstones.js). Гейт по имени таблицы: только у daily-calcs-data
    // есть понятие "delete-protected".
    if (endpoint === 'daily-calcs-data' && Array.isArray(data)) releaseProtectedDcMarks(data);
    // v10.05 → 2026-07-17 (tombstone-контракт): пара processTombstoned+filterTombstoned
    // заменена единой syncServerTombstones — та же обработка + reconcile: если сервер в
    // tombstone-aware ответе (есть массив tombstoned) прислал строку в data — строка
    // живая (admin-restore), stale-запись локального кэша удалений чистится (revive) и
    // строка снова доходит до страниц. Раньше tombstonedIDs не чистился НИКОГДА →
    // восстановленная строка была скрыта в этом браузере навсегда. Гонки/порядок/floor —
    // см. комментарий у syncServerTombstones (src/lib/tombstones.js).
    data = syncServerTombstones(endpoint, { data, tombstoned: json.tombstoned, version: json.version }).data;
    if (Array.isArray(data)) {
      // Only save to localStorage if we got real data back — don't wipe cache with empty response
      // (deletes are handled by apiSave which calls lsSave before the server request)
      if (data.length > 0) {
        lsSave(endpoint, data);
        // v31.17 FIX: seed lastKnownCounts from server — server is single source of truth for count.
        // Prevents "180 → 10" stale ghost where lastKnownCounts остаётся from prior session / stale init
        // и блокирует все последующие saves как "suspicious loss".
        lastKnownCounts[endpoint] = data.length;
      }
    }
    return data;
  } catch (e) {
    // v9.05 FIX: Don't immediately mark offline on a single timeout — could be a flicker
    // Only mark offline if we were already having issues (consecutive failures)
    if (!serverOnline) return null; // Already offline, stay offline
    // First failure — try a quick health check before killing online status
    try {
      const h = await fetch(`${API_BASE}/health`, { signal: AbortSignal.timeout(3000) });
      if (h.ok) { /* Server is fine, just this request failed */ return null; }
    } catch {}
    setServerOnline(false);
    return null;
  }
}

// -- Conflict resolution: when apiSave gets 409, merge server data into React state --
// `export` added (C1): was module-local in App.jsx (in-file caller). Now cross-module —
// App.jsx imports it back for AppInner. Body below is byte-identical to the original.
export function handleConflictResolution(endpoint, serverData, serverVersion, opts) {
  // 1. Update version so next save uses correct version
  if (serverVersion) { dataVersions[endpoint] = serverVersion; savePersistedVersions(dataVersions); }
  // Daily Calcs 409: сервер уже решил, что локальная копия опасна. Замена, не LWW-merge.
  if (opts && opts.authoritative === true) {
    lsSave(endpoint, serverData);
    const authoritativeSetter = conflictSetters[endpoint];
    if (typeof authoritativeSetter === 'function') authoritativeSetter(serverData);
    dispatchToast('Data reloaded from the server — your last edit was not saved, please enter it again', 'warning');
    return;
  }
  // 2. Merge with localStorage and compare before touching React state
  const lsData = lsGet(endpoint, null) || [];
  const setter = conflictSetters[endpoint];
  if (setter && typeof mergeByIDGlobal === 'function') {
    const merged = mergeByIDGlobal(lsData, serverData, endpoint, 'sync');
    lsSave(endpoint, merged);
    if (JSON.stringify(merged) !== JSON.stringify(lsData)) {
      setter(merged);
      console.log(`🔄 Conflict on ${endpoint} — merged server v${serverVersion} into state (changed)`);
    } else {
      console.log(`🔄 Conflict on ${endpoint} — server v${serverVersion} same as local, no state update`);
    }
  } else {
    lsSave(endpoint, serverData);
    console.log(`🔄 Conflict on ${endpoint} — saved server v${serverVersion} to localStorage (no setter)`);
  }
}

// -- Durable rescue store for saves that exhausted the retry budget --
// localStorage key под который складываем save, переживший >5 неудачных flush-попыток.
// Audit H-LC3: см. persistDroppedSave ниже.
export const LS_DROPPED_SAVES_KEY = 'blitz_dropped_saves';

// -- 2026-07-28 (аудит прав): эндпоинты, на которые сервер ответил 403 --
// Нужен, чтобы отличать «нет прав на эту таблицу» от «сервер не ответил»: apiGet в обоих
// случаях отдаёт null, и без этой пометки App.jsx уходит в ветку локальной дозаливки —
// показывает запрещённые строки из кэша и пытается отправить их обратно.
// Пометка снимается при первом успешном ответе (права могли вернуть, перезагрузка не нужна).
const forbiddenEndpoints = new Set();

// Бан по IP тоже отдаёт 403 — на ЛЮБОЙ путь, включая /api/health. Пока причины были
// неразличимы, забаненный IP выглядел как «сотруднику закрыли таблицу»: клиент чистил
// кэш и аварийное хранилище, и несохранённые правки исчезали молча. Сервер помечает
// бан заголовком X-Blitz-Deny: ip-ban (и полем deny в теле, на случай прокси, который
// режет заголовки). Заголовок читается синхронно и не требует разбора тела.
export function isIpBanResponse(res) {
  try {
    return String(res?.headers?.get?.('X-Blitz-Deny') || '') === 'ip-ban';
  } catch { return false; }
}

/** Ответил ли сервер 403 на чтение этой таблицы в текущей сессии вкладки. */
export function isEndpointForbidden(endpoint) {
  return forbiddenEndpoints.has(endpoint);
}

// Вычистить таблицу из аварийного хранилища: если строки успели осесть туда ДО отзыва
// прав, lsRemove их не достанет — это отдельный ключ со своей структурой.
function purgeDroppedSaves(endpoint) {
  try {
    const raw = localStorage.getItem(LS_DROPPED_SAVES_KEY);
    if (!raw) return;
    const list = JSON.parse(raw);
    if (!Array.isArray(list)) return;
    const kept = list.filter(x => x && x.endpoint !== endpoint);
    if (kept.length !== list.length) localStorage.setItem(LS_DROPPED_SAVES_KEY, JSON.stringify(kept));
  } catch { /* приватный режим / битый JSON — не критично */ }
}

// -- 2026-08-28: склад стал невидимым — значит, обязан быть конечным --
// Баннер спасённых правок убран: смерть сессии теперь чинится на причине (пауза очереди
// строк + модалка про протухшую сессию), и окно вылезало практически только на осадке
// за июнь-июль. Но без витрины никто не разгребает склад руками, а рос он без потолка:
// у сотрудника накопилось 22 снимка, среди них customer-payments на 512 строк — таблица,
// доступ к которой ему потом закрыли. purgeDroppedSaves снимает такое только на живом
// 403, а на закрытую страницу браузер больше не ходит. Отсюда два ограничителя.
// Место здесь общее с кэшами таблиц CRM: переполнить localStorage — сломать их.
const DROPPED_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
const DROPPED_MAX_ENTRIES = 10;

// 2026-09-27 (жюри A): потолок по числу вытесняет СНАЧАЛА осадок (без notice) и закрытые
// ('seen'), старейшие первыми. Записи, ждущие отчёта ('new') или показа ('confirmed'), уходят,
// только если иначе в потолок не уложиться — срезать их подряд значило терять свежую потерю.
function capDroppedList(list) {
  if (list.length <= DROPPED_MAX_ENTRIES) return list;
  let excess = list.length - DROPPED_MAX_ENTRIES;
  const pending = e => e && (e.notice === 'new' || e.notice === 'confirmed');
  const drop = new Set();
  for (let i = 0; i < list.length && excess > 0; i++) if (!pending(list[i])) { drop.add(i); excess--; }
  for (let i = 0; i < list.length && excess > 0; i++) if (!drop.has(i)) { drop.add(i); excess--; }
  return list.filter((_, i) => !drop.has(i));
}

/** Подрезать аварийное хранилище: выбросить протухшее, оставить последние N записей. */
function pruneDroppedSaves() {
  try {
    const raw = localStorage.getItem(LS_DROPPED_SAVES_KEY);
    if (!raw) return;
    const list = JSON.parse(raw);
    if (!Array.isArray(list)) return;
    const cutoff = Date.now() - DROPPED_MAX_AGE_MS;
    // Нечитаемая метка времени — НЕ повод удалять спасённые данные: неизвестный возраст
    // это не «старое». Такие записи держит потолок по числу, а не срок годности.
    const fresh = list.filter(e => {
      const t = e && e.droppedAt ? Date.parse(e.droppedAt) : NaN;
      return Number.isNaN(t) || t >= cutoff;
    });
    // Записи лежат в порядке появления — в хвосте самые свежие.
    const kept = capDroppedList(fresh);
    if (kept.length === list.length) return;
    if (kept.length === 0) localStorage.removeItem(LS_DROPPED_SAVES_KEY);
    else localStorage.setItem(LS_DROPPED_SAVES_KEY, JSON.stringify(kept));
  } catch { /* приватный режим / битый JSON — не критично */ }
}

// Один проход на старте вкладки — им и вычищается уже накопленный осадок.
pruneDroppedSaves();

// -- H-LC3 (audit 2026-06-04): durable rescue вместо тихого discard --
// БАГ: flushPendingSaves после 5 неудачных retry делал `pendingSaves.delete(endpoint)`
//      с одним лишь console.warn. При длительном outage (сервер лежит / клиент офлайн
//      дольше ~2.5 мин = 5 циклов setInterval(...,30000)) правка пользователя
//      (статус платежа, изменение сделки, finance-row + её список `deleted`) исчезала
//      БЕЗВОЗВРАТНО — это data-loss. localStorage-снимок (lsSave в apiSave) НЕ спасает
//      надёжно: pendingSaves keyed by endpoint (один слот на таблицу), а в value лежит
//      ещё и `deleted` (список удалённых id) — без доставки на сервер удаление теряется,
//      и последующий 409/refetch может воскресить старые данные поверх локальной правки.
// ПОЧЕМУ так: исходный 5-retry cap нужен чтобы НЕ долбить сеть бесконечно — это поведение
//      сохраняем (запись по-прежнему уходит из активной in-memory retry-очереди). Но вместо
//      выкидывания мы (1) кладём её в durable store localStorage (переживает reload,
//      поддаётся восстановлению), (2) кричим громко: console.error + window CustomEvent
//      'blitz:save-dropped'.
// 2026-08-28: витрины у склада больше нет — баннер убран по решению владельца. Хранилище
//      остаётся тихой страховкой на случай «сервер лежал дольше 2.5 минут»: достать из
//      него можно руками через devtools, а событие ниже — точка подключения, если UI
//      когда-нибудь вернут. Срок годности записям ставит pruneDroppedSaves.
let droppedSeq = 0;

// 2026-09-27 (design v2, P3) — какие причины НИКОГДА не сигналят серверу/сотруднику:
// W4 (сессия умерла — сотрудник и так видит модалку) и W5 (merge-stale-local — сервер
// прав, у клиента просто устаревшая копия, это не потеря). 'users' исключена отдельно
// (пароли/хеши чужим отчётом не читать — то же решение, что и на сервере, save-dropped.cjs).
const SILENT_DROP_REASONS = new Set(['session-expired', 'merge-stale-local']);

export function persistDroppedSave(endpoint, save) {
  // 2026-08-27: `reason` отличает спасение по мёртвой сессии от исчерпанных повторов —
  // текст в консоли перестаёт врать, а спасение сессии не плодит одинаковые записи
  // (flush мог сработать дважды: прямой вызов + уже поставленный таймер).
  const reason = (save && save.reason) || 'retries-exhausted';
  // 2026-09-27: 'rejected' — решённый отказ сервера, повторов не было; «after 5 retries» тут врало.
  console.error(reason === 'session-expired'
    ? `🔴 SAVE_RESCUED (сессия отклонена сервером) → localStorage[${LS_DROPPED_SAVES_KEY}]: ${endpoint}`
    : reason === 'rejected'
      ? `SAVE_REJECTED (HTTP ${save && save.httpStatus}) — rescued to localStorage[${LS_DROPPED_SAVES_KEY}]: ${endpoint}`
      : `🔴 SAVE_DROPPED after 5 retries — rescued to localStorage[${LS_DROPPED_SAVES_KEY}]: ${endpoint}`);
  // Подрезаем ПЕРЕД записью: иначе потолок держался бы только до перезагрузки вкладки.
  pruneDroppedSaves();
  const droppedAt = new Date().toISOString();
  const data = save && save.data;
  const deleted = (save && save.deleted) || [];
  // 2026-09-27 (design v2, P3): достойна ли запись отчёта серверу (реальная свежая
  // потеря, не осадок W4/W5/users, и есть хоть что-то). Записи без notice — осадок,
  // репортёр их никогда не трогает (инвариант 1-2, design v2 §5).
  const rowsPlusDeletes = (Array.isArray(data) ? data.length : 0) + (Array.isArray(deleted) ? deleted.length : 0);
  const eligibleForNotice = !SILENT_DROP_REASONS.has(reason) && endpoint !== 'users' && rowsPlusDeletes > 0;
  const entry = {
    endpoint,
    data,
    deleted,
    userEmail: (save && save.userEmail) || 'unknown',
    reason,
    httpStatus: save && save.httpStatus,
    droppedAt,
    // Собственный ключ: метка времени в миллисекундах может совпасть у двух записей,
    // и удаление восстановленной снесло бы заодно чужую, ещё не спасённую (жюри 27.08).
    id: `${endpoint}|${droppedAt}|${++droppedSeq}`,
  };
  if (eligibleForNotice) entry.notice = 'new';
  try {
    const raw = localStorage.getItem(LS_DROPPED_SAVES_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    let list = Array.isArray(arr) ? arr : [];
    if (reason === 'session-expired') {
      // Вердикт один на жизнь страницы — держим одну актуальную запись на таблицу.
      list = list.filter(e => !(e && e.endpoint === endpoint && e.reason === 'session-expired'));
    }
    list.push(entry);
    // Потолок ещё раз, уже С новой записью: подрезка выше идёт ДО push, и одного её
    // прохода мало — склад разросся бы до N+1.
    localStorage.setItem(LS_DROPPED_SAVES_KEY, JSON.stringify(capDroppedList(list)));
  } catch (e) {
    // localStorage недоступен/переполнен — запись всё равно НЕ должна молча пропасть:
    // громкий лог остаётся, цикл не падает. `entry` (в памяти) всё равно уйдёт с событием
    // ниже — репортёр (dropped-save-reporter.js) читает его из detail, не с диска.
    console.error(`🔴 SAVE_DROPPED rescue could not persist to localStorage for ${endpoint}:`, e);
  }
  // Точка подключения для UI/репортёра. detail.entry — сама запись из ПАМЯТИ (не повторное
  // чтение localStorage — тот мог не сохраниться выше). detail.endpoint сохранён отдельным
  // полем — на нём висят более старые тесты (flush-dropped-save.test.js).
  try {
    if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function' && typeof CustomEvent === 'function') {
      window.dispatchEvent(new CustomEvent('blitz:save-dropped', { detail: { endpoint, entry } }));
    }
  } catch {}
}

// 2026-09-27 (design v2, W2b) — «не плодить одинаковые записи»: последняя запись склада
// по этому endpoint с тем же httpStatus и тем же payload → это debounce/дубль-вызов, а
// не новый отказ. Ищет с хвоста (свежие записи — там, persistDroppedSave делает push).
function isDuplicateDroppedSave(endpoint, data, deleted, httpStatus) {
  try {
    const raw = localStorage.getItem(LS_DROPPED_SAVES_KEY);
    if (!raw) return false;
    const list = JSON.parse(raw);
    if (!Array.isArray(list)) return false;
    for (let i = list.length - 1; i >= 0; i--) {
      const e = list[i];
      if (!e || e.endpoint !== endpoint) continue;
      return e.httpStatus === httpStatus
        && JSON.stringify(e.data ?? null) === JSON.stringify(data ?? null)
        && JSON.stringify(e.deleted ?? []) === JSON.stringify(deleted ?? []);
    }
    return false;
  } catch { return false; }
}

// -- Pending saves queue: retry failed saves when server comes back --
// `export` added (C1): was module-local in App.jsx. Now cross-module — App.jsx imports
// it back for the reconnect-flush effect. Body below is byte-identical to the original
// EXCEPT the H-LC3 fix on the >5-retries branch (durable rescue instead of silent drop).
// 2026-08-31 (Phase 2 «Офлайн видим», П6.1) — module-singleton in-flight lock. Three
// independent callers can race the SAME cycle (App.jsx's 10s reconnect-flush, this
// file's own 30s ticker below, and onLogin) — an overlapping pass can resurrect an
// already-delivered record: pass A's `pendingSaves.set(...)` (retry bump, line ~436)
// landing AFTER pass B's `pendingSaves.delete(...)` (line ~441) for the SAME endpoint
// re-queues a save the server already has. Existing race, fixed by class (one flush
// cycle in flight at a time) rather than patching each caller.
let flushInFlight = false;

export async function flushPendingSaves() {
  if (flushInFlight) return;
  flushInFlight = true;
  try {
    if (pendingSaves.size === 0) return;
    // 2026-08-31 (P5-QUEUE, тихая потеря правок): зеркало row-queue's q.paused — сессия уже
    // признана мёртвой в этой вкладке, жечь об неё "решённые" попытки незачем. Очередь ждёт
    // вход: App.jsx onLogin будит её явно (flushPendingSaves() рядом с resumeRowQueues()).
    if (isSessionKnownDead()) return;
    // v11.02 M2: drop saves that have failed too many times
    const token = getSessionToken();
    if (!token) return;
    // 2026-08-31 (Phase 2, П6.2) — probe-first: a locally-expired session (same 23h TTL
    // App.jsx's pulse checks) shouldn't burn a flush cycle hammering the queue's endpoints
    // before knowing whether the session is even still good. Fire-and-forget (no await) —
    // confirmSessionExpired dedupes via confirmingEpoch and awaiting here guarantees
    // nothing anyway; the verdict lands on the NEXT tick: alive -> loginTime extended,
    // flush proceeds normally; dead -> isSessionKnownDead() pauses future cycles. Retry
    // counts of already-queued records are untouched either way.
    if (localSessionExpired(getRawSession(), Date.now())) {
      checkLocalSessionExpiry('pre-flush');
      return;
    }
    const endpoints = [...pendingSaves.keys()];
    for (const endpoint of endpoints) {
      // v11.02 M2: stop active retrying after 5 attempts to prevent infinite hammering.
      // H-LC3 fix: НЕ выбрасываем запись молча — спасаем в durable store + громкий сигнал
      // (persistDroppedSave). Запись по-прежнему уходит из активной очереди (pendingSaves.delete),
      // так что бесконечного долбления сети нет, но данные пользователя НЕ теряются.
      // 2026-07-30: запись могла попасть в очередь ДО того, как таблица стала запрещённой
      // (или 403 пришёл на другую попытку). Без этой проверки очередь дожала бы свои 5
      // повторов и на пятом положила запрещённые finance-строки в blitz_dropped_saves —
      // ровно то, что закрывает 403-ветка в apiSave. Здесь именно drop, НЕ persistDroppedSave:
      // спасать данные, к которым у сотрудника нет прав, незачем.
      if (isEndpointForbidden(endpoint)) {
        console.warn(`⛔ flushPendingSaves: очередь для "${endpoint}" сброшена — нет прав на таблицу`);
        pendingSaves.delete(endpoint);
        purgeDroppedSaves(endpoint);
        continue;
      }
      const save = pendingSaves.get(endpoint);
      if (!save) continue;
      if ((inFlightSaves[endpoint] || 0) > 0) continue;
      // v11.02 M2: stop active retrying after 5 attempts to prevent infinite hammering.
      // 2026-09-27 (design v2, W1): save.lastStatus — последний РЕШЁННЫЙ статус (см. бамп
      // ниже) — идёт в httpStatus rescue-записи; на retries>=5 у нас никакого fetch этого
      // цикла ещё не было, статус только тот, что накопила очередь с прошлых попыток.
      if ((save.retries || 0) >= 5) { persistDroppedSave(endpoint, { ...save, httpStatus: save.lastStatus }); pendingSaves.delete(endpoint); continue; }
      const sentGen = save.gen;
      try {
        const sentToken = getSessionToken();
        const { res, timing: flushTiming } = await fetchAndTime(`${API_BASE}/${endpoint}`, {
          method: 'POST', headers: authHeaders(),
          body: JSON.stringify({ data: save.data, version: dataVersions[endpoint] || 0, user: save.userEmail || 'unknown', deleted: save.deleted || [] }),
          signal: AbortSignal.timeout(5000),
        });
        // 2026-08-31 (P5-QUEUE): retries++ used to sit BEFORE the fetch and burn blind — a
        // network throw, a 429, or a 5xx spent an attempt with the server never having
        // rendered a verdict on the DATA. A rough-but-passing outage could "use up" (and
        // eventually rescue-out) an edit the server never even saw. Now it advances ONLY on a
        // decided response (any status except 429/5xx) — those two, and a network throw
        // (caught below), leave the record queued at the SAME count, retried next tick for
        // as long as the outage lasts.
        // Retry-bump не меняет gen (иначе flush перекрыл бы собственное удаление).
        // 200/throttled retries не тратит: штатный throttle — не отказ.
        if (res.status === 403) {
          if (isIpBanResponse(res)) {
            console.warn(`⛔ flushPendingSaves ${endpoint}: бан по IP — запись остаётся в очереди`);
            setServerOnline(false);
            continue;
          }
          console.warn(`⛔ flushPendingSaves ${endpoint}: 403 — таблица запрещена, очередь снята`);
          forbiddenEndpoints.add(endpoint);
          lsRemove(endpoint);
          purgeDroppedSaves(endpoint);
          pendingSaves.delete(endpoint);
          continue;
        }
        // 2026-09-27 (design v2, W1): 401 тоже не тратит попытку — это вердикт о СЕССИИ, не
        // о данных (см. ветку res.status===401 ниже). 408 — тратит (жюри A): пять таймаутов
        // подряд за 2.5 мин — устойчивая проблема, без счёта запись не дошла бы до склада.
        // lastStatus — последний решённый статус — отдаётся в httpStatus на >=5-retries rescue выше.
        if (!isPendingStale(endpoint, sentGen) && res.status !== 429 && res.status !== 401 && res.status < 500 && !res.ok) {
          const live = pendingSaves.get(endpoint);
          if (live) pendingSaves.set(endpoint, { ...live, retries: (live.retries || 0) + 1, lastStatus: res.status });
        }
        if (res.ok) {
          // 2026-08-31 (Phase 2 «Офлайн видим», П6.3) — this branch was the one successful
          // path through flushPendingSaves with NO online-flip: every other success site in
          // this file (apiGet/apiSave/WS onopen) already calls setServerOnline(true). A
          // successful queue drain is equally strong evidence the server is back — the
          // OfflineBanner/SyncStatus verdict must clear on it too, not wait for some OTHER
          // request to happen to succeed first.
          setServerOnline(true);
          const json = await res.json();
          noteHttpResponseSample(res, json, flushTiming);
          if (json.throttled === true) {
            continue;
          }
          // Сервер принял запись — его версия правда независимо от очереди.
          // Поколение охраняет только мутации очереди.
          if (json.version) { dataVersions[endpoint] = json.version; savePersistedVersions(dataVersions); }
          rememberDelivered(endpoint, sentGen, save.data);
          if (isPendingStale(endpoint, sentGen)) continue;
          // 2026-09-01 (фаза3 П5) — deleted-политика — «досылать всегда» (см. unionQueuedDeleted
          // выше): громкость вместо тихости. save.deleted — снимок ДО pendingSaves.delete ниже,
          // тот же массив, что ушёл в теле POST.
          if (save.deleted && save.deleted.length > 0) {
            dispatchToast(`Applying ${save.deleted.length} queued deletion(s) to ${endpoint}`, 'info');
          }
          dropPendingIfGenAtMost(endpoint, sentGen, { data: save.data, deleted: save.deleted || [] });
          console.log(`✅ Flushed pending save: ${endpoint}`);
        } else if (res.status === 409) {
          // v9.15: Conflict — our data is stale, discard and re-fetch from server
          // PR R (sub_R3, 28.09): body нужен ДО early-continue ниже — authoritative на
          // daily-calcs-data значит "сервер не применил ни одного deleted из ЭТОЙ
          // попытки", и это окончательный факт для save.deleted независимо от того,
          // обогнала ли очередь эту попытку новым намерением (isPendingStale) — тот
          // факт про НОВУЮ попытку, не отменяет отказ по СТАРОЙ. См. tombstones.js
          // releaseProtectedDcMarks/untrackDelete.
          let body = null;
          try { body = await res.json(); } catch {}
          if (endpoint === 'daily-calcs-data' && body && body.authoritative === true && Array.isArray(save.deleted)) {
            for (const id of save.deleted) untrackDelete(endpoint, id);
          }
          if (isPendingStale(endpoint, sentGen)) continue;
          dropPendingIfGenAtMost(endpoint, sentGen);
          console.log(`⚠️ Stale data for ${endpoint} — discarding and will re-fetch`);
          if (body && body.error === "stale_data") {
            dispatchToast(`⚠️ Refreshing ${endpoint} — server has newer data`, 'warning');
            // Force page reload to get fresh data
            setTimeout(() => window.location.reload(), 1500);
          }
        } else if (res.status === 400) {
          // FR6 (fix-round 2026-08-31, жюри A HIGH-1): was a silent discard on the FIRST
          // 400 — no retries counter, no persistDroppedSave, unlike the >=5-retries path
          // (H-LC3) which already treats a decided terminal rejection as a rescue, not a
          // silent drop. A 400 here is a decided answer (server permanently refuses this
          // payload), so the record is gone from the active queue either way — the fix
          // durably saves it first, same contract as the retry-cap branch above.
          if (isPendingStale(endpoint, sentGen)) continue;
          // 2026-09-27 (design v2, W1): было — reason по умолчанию 'retries-exhausted' даже
          // на самом первом 400 (враньё в консоли/на складе — отказ решённый, не исчерпание
          // попыток). Явный reason:'rejected' + httpStatus:400 — то же, что W2b/W6 ниже.
          persistDroppedSave(endpoint, { ...save, reason: 'rejected', httpStatus: 400 });
          dropPendingIfGenAtMost(endpoint, sentGen);
          console.log(`⚠️ Removed blocked pending save for ${endpoint} — rescued to blitz_dropped_saves`);
        } else if (res.status === 401) {
          // 2026-08-18: очередь повторов молча крутилась 5 попыток и уходила в
          // blitz_dropped_saves, не сказав ни слова про мёртвую сессию. Запись из очереди
          // НЕ трогаем — её судьбу по-прежнему решает счётчик повторов; сообщаем только
          // о сессии.
          // S3b: 401 старого токена не хоронит новую сессию; запись остаётся в очереди.
          if (getSessionToken() === sentToken) {
            confirmSessionExpired({ token: sentToken, reason: `flush:${endpoint}` });
          }
        }
      } catch {}
    }
  } finally {
    flushInFlight = false;
  }
}
// Try to flush pending saves every 30 seconds
setInterval(flushPendingSaves, 30000);

// -- v31.89: Payment action telemetry beacon (sendBeacon, fire-and-forget) --
export function logPaymentAction(table, paymentId, invoice, fromStatus, toStatus) {
  try {
    // PR #140: embed token в body (sendBeacon can't set Authorization header).
    // Backend's telemetry route extracts `_t` via bodyTokenAdapter → adapts к Authorization.
    // Use getSessionToken() (not raw localStorage) — covers blitz_session JSON fallback
    // (line 1085) для cases where blitz_token is empty but session persisted otherwise.
    // Sub-agent PR #140 review finding #2: avoids asymmetric token-source vs authHeaders().
    const token = getSessionToken() || '';
    const payload = { table, paymentId, invoice, fromStatus, toStatus, _t: token };
    const body = JSON.stringify(payload);
    const url = `${API_BASE}/telemetry/payment-action`;
    // sendBeacon path — guaranteed-queue delivery, не throttled on mobile background.
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const blob = new Blob([body], { type: 'application/json' });
      const queued = navigator.sendBeacon(url, blob);
      if (queued) return; // browser accepted к queue — done.
      // fall-through к fetch if sendBeacon refused (rare — typically size-cap or CSP)
    }
    fetch(url, {
      method: 'POST',
      headers: authHeaders(),
      body,
      keepalive: true,
    }).catch(() => {});
  } catch (_) { /* silent */ }
}

// -- 2026-06-09: keepalive flush — survives page unload (reload/close) --
//
// A normal fetch is CANCELLED when the tab unloads, and the 3s apiSave throttle defers a rapid
// trailing save to a setTimeout retry whose timer DIES on reload — so the very last of a burst of
// edits could be stranded (owner hid Selling countries, the final hide was lost on refresh). A
// `keepalive` fetch is kept alive by the browser past unload (≤64KB body) AND can set the
// Authorization header (unlike sendBeacon, which is why logPaymentAction embeds the token in the
// body). Callers wire this into a `pagehide` handler and pass the FULL latest list. Fire-and-forget:
// a stale-version 409 is harmless (the in-flight normal save, if any, still lands).
export function keepaliveSave(endpoint, data, userEmail) {
  try {
    if (!Array.isArray(data)) return;
    lsSave(endpoint, data); // keep the local safety net consistent with what we flush
    const body = JSON.stringify({ data, version: dataVersions[endpoint] || 0, user: userEmail || 'unknown', deleted: [] });
    fetch(`${API_BASE}/${endpoint}`, { method: 'POST', headers: authHeaders(), body, keepalive: true }).catch(() => {});
  } catch (_) { /* silent — best effort */ }
}

// FR1 (fix-round 2026-08-31, жюри A CRITICAL-1): re-queuing into pendingSaves must UNION
// `deleted` with whatever is already queued for this endpoint, never overwrite it.
// getAndClearDeletes (tombstones.js) drains deletedIDs UNCONDITIONALLY at call time,
// regardless of whether the save reaches the server — so a delete queued by an earlier
// failed attempt (401 / throttle / network) would otherwise be silently discarded the
// moment ANY other edit to the same table also fails to deliver: the server never learns
// about that deletion, and the row survives there forever despite having been deleted
// client-side. Union is safe — there is no undelete flow, and a resurrected id is blocked
// server-side by tombstones regardless of what this queue does.
// 2026-09-01 (фаза3 П5, свод критики — координаторское решение вместо design D3-2) —
// deleted-политика фазы 3: намерения удаления досылаются ВСЕГДА, сколько бы ни просидели
// в очереди. Никакого queuedAt/per-id меток/возрастного среза — рассмотренный и ОТВЕРГНУТЫЙ
// вариант (24ч-порог, критик cursor) свод отклонил: позднее ложное надгробие громко и
// обратимо (tombstones TTL 90d + admin bulk-sync-silent restore + finance-audit diff), а
// тихо отрезанное реальное удаление — тихое воскрешение записи, для финансов хуже (неверный
// баланс в отчётах, никто не знает, что чинить). Симметрично решению владельца №3 про
// данные («досылать, сервер защищён»). Громкость вместо тихости обеспечена ниже во
// flushPendingSaves (dispatchToast на доставке deleted), не здесь.
function unionQueuedDeleted(endpoint, deleted) {
  const prevDeleted = pendingSaves.get(endpoint)?.deleted;
  return [...new Set([...(prevDeleted || []), ...(deleted || [])])];
}

// Один отложенный throttle-повтор на endpoint: и клиентское окно, и json.throttled.
// Таймер снимается при срабатывании; не сериализуется.
/** @type {Record<string, { id: ReturnType<typeof setTimeout>, delay: number }>} */
const throttleRetries = Object.create(null);

/** POST из apiSave: от первого fetch через паузу 2с до конца разбора ответа повтора; не flush / keepalive / row-save. */
const inFlightSaves = Object.create(null);

/** Последний успешно доставленный снимок: { gen, data }. Не LS. */
const lastDelivered = Object.create(null);

function rememberDelivered(endpoint, gen, data) {
  const prev = lastDelivered[endpoint];
  if (prev && (prev.gen || 0) > (gen || 0)) return;
  lastDelivered[endpoint] = { gen, data };
}

function beginInFlightSave(endpoint) {
  inFlightSaves[endpoint] = (inFlightSaves[endpoint] || 0) + 1;
}

function endInFlightSave(endpoint) {
  const n = (inFlightSaves[endpoint] || 0) - 1;
  if (n <= 0) delete inFlightSaves[endpoint];
  else inFlightSaves[endpoint] = n;
}

function scheduleThrottleRetry(endpoint, delayMs, userEmail, isRetry) {
  if (throttleRetries[endpoint]) return;
  const delay = Math.min(3100, Math.max(100, delayMs));
  const id = setTimeout(() => {
    delete throttleRetries[endpoint];
    try {
      const live = pendingSaves.get(endpoint);
      if (!live || live.gen !== currentSaveIntent(endpoint)) return;
      // Как flush (H4): не слать, пока POST этой таблицы ещё в полёте.
      // Запись не трогаем — её заменит G1 / queueUserSave, доставит flush.
      if ((inFlightSaves[endpoint] || 0) > 0) return;
      Promise.resolve(apiSave(endpoint, live.data, live.userEmail || userEmail, isRetry)).catch(() => {});
    } catch {}
  }, delay);
  throttleRetries[endpoint] = { id, delay };
}

export function _testPendingThrottleTimerCount(endpoint) {
  if (endpoint) return throttleRetries[endpoint] ? 1 : 0;
  return Object.keys(throttleRetries).length;
}

export function _testPendingThrottleDelay(endpoint) {
  return throttleRetries[endpoint] ? throttleRetries[endpoint].delay : null;
}

const saveIntentGen = Object.create(null);

export function takeSaveIntent(endpoint) {
  saveIntentGen[endpoint] = (saveIntentGen[endpoint] || 0) + 1;
  return saveIntentGen[endpoint];
}

export function currentSaveIntent(endpoint) {
  return saveIntentGen[endpoint] || 0;
}

function isPendingStale(endpoint, sentGen) {
  // Нет записи ≠ устаревший: собственное удаление не должно глушить тост/reload.
  // Устаревший — только живая запись с иным поколением.
  const live = pendingSaves.get(endpoint);
  if (!live) return false;
  return live.gen !== sentGen;
}

function dropPendingIfGenAtMost(endpoint, gen, sent) {
  const live = pendingSaves.get(endpoint);
  if (!live) return;
  if ((live.gen || 0) > (gen || 0)) return;
  // Без sent — прежнее поведение (409/400: запись целиком снимается).
  // С sent — снимаем только те deleted, которые этот POST реально нёс; остаток
  // досылается, data = доставленный снимок (не старее успеха).
  if (!sent) {
    pendingSaves.delete(endpoint);
    return;
  }
  const sentDeleted = new Set(sent.deleted || []);
  const rest = (live.deleted || []).filter((id) => !sentDeleted.has(id));
  if (rest.length === 0) {
    pendingSaves.delete(endpoint);
    return;
  }
  pendingSaves.set(endpoint, { ...live, data: sent.data, deleted: rest, gen });
}

/** Новые данные пользователя — только если нет более новой записи; deleted всегда union. */
function queueUserSave(endpoint, { data, userEmail, deleted, gen, spread }) {
  const live = pendingSaves.get(endpoint);
  if (live && (live.gen || 0) > gen) {
    pendingSaves.set(endpoint, {
      ...live,
      deleted: unionQueuedDeleted(endpoint, deleted),
    });
    return;
  }
  if (!live && gen < currentSaveIntent(endpoint)) {
    const rest = unionQueuedDeleted(endpoint, deleted);
    if (rest.length === 0) return;
    const intent = currentSaveIntent(endpoint);
    const delivered = lastDelivered[endpoint];
    // Если lastDelivered.gen совпадает с текущим намерением — это снимок B, а не кэш LS
    // (туда мог записаться задержанный GET). Иначе более новое намерение ещё в полёте:
    // lsGet — временное значение (его заменит успех G1 / отказ queueUserSave);
    // flush (H4) и throttle-таймер не отправляют, пока inFlightSaves > 0
    // (H9: счётчик держится и на паузе 2с между попытками).
    const payload = (delivered && delivered.gen === intent)
      ? delivered.data
      : lsGet(endpoint, data);
    pendingSaves.set(endpoint, {
      data: payload,
      userEmail,
      deleted: rest,
      gen: intent,
    });
    return;
  }
  if (spread) {
    pendingSaves.set(endpoint, {
      ...(live || {}),
      data,
      userEmail,
      deleted: unionQueuedDeleted(endpoint, deleted),
      gen,
    });
  } else {
    pendingSaves.set(endpoint, {
      data,
      userEmail,
      deleted: unionQueuedDeleted(endpoint, deleted),
      gen,
    });
  }
}

export function enqueuePendingSave(endpoint, { data, userEmail, deleted }) {
  const myGen = takeSaveIntent(endpoint);
  queueUserSave(endpoint, { data, userEmail, deleted: deleted || [], gen: myGen, spread: false });
  return myGen;
}

export function _testResetSaveFlowState() {
  for (const key of Object.keys(throttleRetries)) {
    clearTimeout(throttleRetries[key].id);
    delete throttleRetries[key];
  }
  for (const key of Object.keys(saveIntentGen)) delete saveIntentGen[key];
  for (const key of Object.keys(inFlightSaves)) delete inFlightSaves[key];
  for (const key of Object.keys(lastDelivered)) delete lastDelivered[key];
  flushInFlight = false;
}

export async function apiSave(endpoint, data, userEmail, _isRetry = false) {
  // SAFETY: Never save empty array to server unless there are explicit deletes
  const hasPendingDeletes = deletedIDs[endpoint] && deletedIDs[endpoint].size > 0;
  const pendingDeleteCount = hasPendingDeletes ? deletedIDs[endpoint].size : 0;
  if (Array.isArray(data) && data.length === 0 && !hasPendingDeletes) {
    console.warn(`⚠️ BLOCKED empty save to ${endpoint} — no pending deletes, likely a bug`);
    return false;
  }

  // v31.17 FIX: simple endpoints (gen-balances, today-transactions, tx-archive, ds-archive,
  // daily-summary, blitz-reports*) делают dedup-at-load на клиенте — rows.length ВСЕГДА меньше
  // чем прилетает с сервера (если сервер накопил duplicates). Старый 20% guard считал это
  // "crash-induced data loss" и блокировал save навсегда (180→10 призрак, dead lock в gen-balances).
  // Backend уже имеет empty-save protection для этих routes (server.cjs:1398 protectedRoutes).
  // Frontend guard для них redundant + вреден → skip.
  const SIMPLE_ENDPOINTS_NO_GUARD = new Set(['gen-balances', 'today-transactions', 'tx-archive', 'ds-archive', 'daily-summary', 'blitz-reports', 'blitz-reports-archive', 'invoices', 'invoice-settings', 'selling-offers', 'selling-settings']);
  const skipGuard = SIMPLE_ENDPOINTS_NO_GUARD.has(endpoint);

  // SAFETY v2: Block saves that lose too many records at once (crash protection)
  // If we previously had N records and now have much fewer, something went wrong
  if (!skipGuard && Array.isArray(data) && lastKnownCounts[endpoint] > 0) {
    const prev = lastKnownCounts[endpoint];
    const curr = data.length;
    const maxAllowedDrop = Math.max(prev * 0.2, pendingDeleteCount + 2); // v9.09: stricter 20% drop (was 30%)
    if (prev - curr > maxAllowedDrop) {
      console.error(`🛑 BLOCKED suspicious save to ${endpoint}: ${prev} → ${curr} records (drop of ${prev - curr}, only ${pendingDeleteCount} explicit deletes). This looks like a crash-induced data loss.`);
      // v9.09: Attempt to recover from server before giving up
      try {
        const serverData = await apiGet(endpoint);
        if (Array.isArray(serverData) && serverData.length > curr) {
          console.log(`🔄 Recovering ${endpoint} from server: ${serverData.length} records`);
          lsSave(endpoint, serverData);
          lastKnownCounts[endpoint] = serverData.length;
        }
      } catch {}
      return false;
    }
  }
  // SAFETY v3: If we have NO baseline yet, check localStorage for a count
  // This catches the case after version upgrade when lastKnownCounts is empty
  if (!skipGuard && Array.isArray(data) && !lastKnownCounts[endpoint]) {
    const lsData = lsGet(endpoint, null);
    if (Array.isArray(lsData) && lsData.length > 0) {
      lastKnownCounts[endpoint] = lsData.length;
      // Now apply the same guard
      if (lsData.length > 10 && data.length < lsData.length * 0.3 && pendingDeleteCount === 0) { // v9.05: 30% (was 50%)
        console.error(`🛑 BLOCKED suspicious save to ${endpoint}: localStorage has ${lsData.length} but saving only ${data.length}. Likely stale data after version upgrade.`);
        return false;
      }
    }
  }
  // Update last known count after safety check passes
  if (Array.isArray(data) && data.length > 0) {
    lastKnownCounts[endpoint] = data.length;
  }

  if (isEndpointForbidden(endpoint)) {
    console.warn(`⛔ apiSave пропущен: нет прав на таблицу "${endpoint}"`);
    return false;
  }

  const myGen = takeSaveIntent(endpoint);

  // FIX C1: Debounce — don't save same table within 1 second
  const elapsed = elapsedSince(lastSaveTimestamps[endpoint]);
  if (!hasPendingDeletes && elapsed < 3000) {
    // v31.97 Bug #54: silent-drop fix — was `return true` without lsSave/fetch (data lost).
    // Now update localStorage immediately (browser safety) + schedule deferred retry past
    // throttle window. Preserves Android IP-switch storm protection (still no network burst)
    // while ensuring no silent data drop.
    lsSave(endpoint, data);
    const retryAfter = Math.min(3100, Math.max(100, 3000 - elapsed + 100));
    // 2026-06-09 / 2026-09-16: deferred retry sends only the live queue entry whose gen is
    // the current intent. Rapid edits used to each carry their own snapshot and land out of
    // order (hid to 81, reload showed 74). LS is a cache (GET/WS/post-save may write a stale
    // server snapshot), so the timer must not apiSave(lsGet(...)): if the queue is empty or a
    // newer intent is in flight, send nothing — the newer intent or flush delivers.
    // Один таймер на endpoint — иначе скачок часов назад ставил бы retry через ~5ч.
    scheduleThrottleRetry(endpoint, retryAfter, userEmail, false);
    // 2026-08-31 (P1-THROTTLE, тихая потеря правок): this call has NOT been confirmed by the
    // server — `return true` here lied to App.jsx's performSave guard (F1, 2026-05-16), which
    // took it as proof of delivery and marked the edit "saved" (silencing the next identical
    // debouncedSave) even though nothing reached the DB. The setTimeout above is a happy-path
    // shortcut, not a delivery guarantee — the tab can close before it fires. Queuing into
    // pendingSaves is the actual guarantee (flushPendingSaves' 30s ticker / reconnect-flush).
    // `hasPendingDeletes` gates this whole branch (see condition above) — THIS call never
    // has new deletes of its own, but an earlier failed attempt might already have some
    // queued for this endpoint (FR1) — union instead of the old hardcoded `[]`.
    // spread:false — как раньше свежий объект, retries здесь не тащим (см. отчёт r1).
    queueUserSave(endpoint, { data: lsGet(endpoint, data), userEmail, deleted: [], gen: myGen, spread: false });
    return false;
  }
  lastSaveTimestamps[endpoint] = stampNow();

  // Collect any explicit deletes for this table
  // v11.02 C2 FIX: capture deleted ONCE before first attempt so retry reuses same list
  // 2026-07-28 (аудит прав) — ГЛАВНЫЙ ШОВ. Если сервер уже отказал в правах на эту
  // таблицу, отправлять нечего: запись всё равно получит 403 от requireTableAccess.
  // Выходим ДО lsSave — иначе запрещённые строки вернулись бы в кэш (сводя на нет
  // lsRemove в apiGet), а после 5 неудачных попыток осели бы в blitz_dropped_saves.
  // Проверка стоит здесь, а не у вызывающего, чтобы дыра закрылась независимо от того,
  // кто позовёт (дозаливка App.jsx, debouncedSave, ручной retry).
  const deleted = unionQueuedDeleted(endpoint, getAndClearDeletes(endpoint));

  // ALWAYS save to localStorage first — this is the safety net
  lsSave(endpoint, data);
  // Then push to server with version info + auth token + deleted IDs
  try {
    const sentToken = getSessionToken();
    beginInFlightSave(endpoint);
    const { res, timing: saveTiming } = await fetchAndTime(`${API_BASE}/${endpoint}`, {
      method: 'POST', headers: authHeaders(),
      body: JSON.stringify({ data, version: dataVersions[endpoint] || 0, user: userEmail || 'unknown', deleted }),
      signal: AbortSignal.timeout(5000),
    });
    if (res.status === 401) {
      if (!justLoggedIn && getSessionToken() === sentToken) {
        // См. apiGet выше: снимок сессии до стирания (2026-08-18).
        const killedToken = sentToken;
        const killedSession = localStorage.getItem('blitz_session');
        setSessionToken(null);
        localStorage.removeItem('blitz_session');
        setSessionExpiredFlag(true);
        confirmSessionExpired({
          token: killedToken,
          reason: `api-save:${endpoint}`,
          onAlive: () => restoreKilledSession(killedToken, killedSession),
        });
      }
      // 2026-08-31 (P5-QUEUE): preserve an already-queued `retries` count instead of
      // building a fresh object — this endpoint may already be sitting in pendingSaves
      // from an earlier flushPendingSaves cycle, and overwriting it here silently reset
      // the counter to undefined/0, making the 5-attempts cap non-deterministic.
      // FR1 (fix-round 2026-08-31, жюри A CRITICAL-1): the spread above already carries
      // any previously queued `deleted`, but the plain `deleted` key here (this call's OWN
      // fresh getAndClearDeletes result) unconditionally overwrote it — union instead.
      queueUserSave(endpoint, { data, userEmail, deleted, gen: myGen, spread: true });
      return false;
    }
    if (res.status === 409) {
      // Server rejected — our version is stale. Merge server data into our state.
      try {
        const conflict = await res.json();
        // Более новое намерение: не вливать снимок старого ответа (симметрия post-save sync).
        if (currentSaveIntent(endpoint) <= myGen && conflict.serverData && Array.isArray(conflict.serverData)) {
          const authoritative = conflict.authoritative === true;
          handleConflictResolution(
            endpoint,
            conflict.serverData,
            conflict.serverVersion,
            authoritative ? { authoritative: true } : undefined,
          );
          // PR R (sub_R3, 28.09): authoritative на daily-calcs-data = сервер отклонил
          // ВЕСЬ запрос целиком (guard'а daily-calcs-post-guard.cjs) — включая любые
          // deleted id, которые этот запрос нёс. `deleted` уже отправлен серверу и не
          // изменится задним числом — сервер точно НЕ применил ни одного из них,
          // независимо от конкретной причины отказа (delete_protected и другие делят
          // тот же authoritative-конверт). Снимаем клиентскую метку, иначе защищённая
          // строка осталась бы скрыта в этой вкладке навсегда (см. tombstones.js).
          if (authoritative && endpoint === 'daily-calcs-data' && Array.isArray(deleted)) {
            for (const id of deleted) untrackDelete(endpoint, id);
          }
        }
      } catch {}
      dropPendingIfGenAtMost(endpoint, myGen); // Don't retry stale data
      return false;
    }
    // 2026-07-30 — ВТОРАЯ ПОЛОВИНА ШВА: apiSave учится на СВОЁМ 403.
    //
    // Гейт выше (apiGet ~line 152) наполняет forbiddenEndpoints только из отказа
    // НА ЧТЕНИИ. Если запись получила 403 без предшествующего GET-отказа (страница
    // не открывалась, а фоновая дозаливка из localStorage всё равно сработала),
    // клиент ничего не узнавал — 403 падал в общий retry ниже как сбой сети.
    //
    // Цена на проде (blitz-api-error.log, 30.07): zack@ дал РОВНО 7 отказов на запись
    // customer-payments = 1 запрос + 1 inline-повтор (catch, 2s) + 5 из
    // flushPendingSaves. После пятого persistDroppedSave кладёт ПОЛНУЮ копию
    // запрещённой finance-таблицы в blitz_dropped_saves — та же утечка, которую жюри
    // 28.07 закрыло только на пути чтения.
    //
    // Зеркалим apiGet: помечаем, чистим кэш И аварийное хранилище, снимаем из очереди
    // повторов, НЕ ретраим. Проверка стоит ДО общего `!res.ok`, иначе 403 успел бы
    // уйти в throw.
    if (res.status === 403) {
      // Бан по IP: правки НЕ выбрасываем. Запись остаётся в очереди повторов и в
      // localStorage, человек её не теряет — вернут доступ, и она уедет.
      if (isIpBanResponse(res)) {
        console.warn(`⛔ apiSave ${endpoint}: доступ с этого адреса закрыт (бан по IP) — правка сохранена локально и остаётся в очереди`);
        setServerOnline(false);
        // В localStorage запись уже легла до запроса. В очередь ставим тем же способом,
        // что и ветка 401: сохраняем накопленный счётчик попыток и объединяем удаления,
        // иначе повторная постановка обнулила бы кап в пять попыток и потеряла бы
        // очередь удалений.
        queueUserSave(endpoint, { data, userEmail, deleted, gen: myGen, spread: true });
        return false;
      }
      console.warn(`⛔ apiSave ${endpoint}: сервер отказал в правах (403) — таблица помечена запрещённой, повторов не будет`);
      forbiddenEndpoints.add(endpoint);
      lsRemove(endpoint);            // чужие данные не должны оставаться в кэше браузера
      purgeDroppedSaves(endpoint);   // ...и в аварийном хранилище тоже
      pendingSaves.delete(endpoint); // forbidden-403: любое поколение
      return false;
    }
    if (!res.ok) {
      // Log but don't crash on 400 errors (e.g. empty array protection)
      const errBody = await res.json().catch(() => ({}));
      console.warn(`⚠️ apiSave ${endpoint} got ${res.status}:`, errBody.error || 'unknown');
      if (res.status === 400) {
        // 2026-09-27 (design v2, W2b): было — молчаливый `return false`, ни следа на
        // складе (в отличие от flushPendingSaves' >5-retries/400 путей, H-LC3/FR6). Debounce
        // (elapsed<3000) может повторно звать apiSave с ТЕМ ЖЕ payload — не плодим дубли.
        if (!isDuplicateDroppedSave(endpoint, data, deleted, 400)) {
          persistDroppedSave(endpoint, { data, deleted, userEmail, reason: 'rejected', httpStatus: 400 });
        }
        return false; // Don't retry 400s
      }
      throw new Error('save failed');
    }
    const json = await res.json();
    noteHttpResponseSample(res, json, saveTiming);
    if (json.version) {
      dataVersions[endpoint] = json.version;
      savePersistedVersions(dataVersions);
    }
    // v10.06: Process tombstoned IDs from server — purge zombie records locally
    // Более новое намерение: не откатывать LS/state (версию сервера выше уже применили).
    if (currentSaveIntent(endpoint) <= myGen && json.tombstoned && json.tombstoned.length > 0) {
      processTombstoned(endpoint, json.tombstoned);
      // v10.06: Actively purge zombies from React state immediately
      const setter = conflictSetters[endpoint];
      if (setter && tombstonedIDs[endpoint] && tombstonedIDs[endpoint].size > 0) {
        setter(prev => {
          if (!Array.isArray(prev)) return prev;
          const filtered = prev.filter(r => !tombstonedIDs[endpoint].has(r.id));
          if (filtered.length < prev.length) {
            console.log(`🪦 [${endpoint}]: Purged ${prev.length - filtered.length} zombies from React state`);
            lsSave(endpoint, filtered);
            lastKnownCounts[endpoint] = filtered.length;
          }
          return filtered;
        });
      }
    }
    // v10.05: Handle throttled response — server accepted but skipped actual write
    //
    // 2026-05-19 PR — silent data loss fix. Sub-agent investigation of CLICK_NO_TRANSITION
    // anomaly (Y Admin #Selection 06:30 UTC) found that когда rapid clicks land within
    // server's 3-second throttle window → trailing POSTs return `throttled:true` →
    // client treated as success → save lost forever. Symptom: status stayed Open в DB
    // despite UI showing Received. PAYMENT_RECEIVED notification never fired.
    //
    // Fix: schedule exactly ONE retry ~3.2s later (server throttle window 3s + 200ms
    // buffer). `_isRetry` flag ensures retry-of-retry doesn't ping-pong infinitely —
    // if retry also throttled, give up (acceptable: debouncedSave useEffect will re-fire
    // с full state shortly after, providing additional natural retry).
    if (json.throttled) {
      setServerOnline(true);
      // 2026-08-31 (P1-THROTTLE, тихая потеря правок): was `pendingSaves.delete(endpoint)` +
      // `return true` — the server explicitly said it did NOT write this POST, yet the caller
      // (App.jsx performSave's F1 guard, 2026-05-16) took `true` as confirmed delivery and
      // marked the edit "saved", silencing the next identical debouncedSave. The 3.2s retry
      // below is a happy-path shortcut, not a delivery guarantee (tab can close first) — queue
      // instead of delete so flushPendingSaves' 30s ticker guarantees delivery either way.
      // FR1 (fix-round 2026-08-31, жюри A CRITICAL-1): no spread at all here — `deleted`
      // (this call's own fresh getAndClearDeletes result) unconditionally overwrote
      // whatever was already queued — union instead.
      queueUserSave(endpoint, { data, userEmail, deleted, gen: myGen, spread: false });
      if (!_isRetry) {
        // Capture closure for retry — log message helps observability in DevTools console.
        console.log(`🔁 [${endpoint}] throttled — retrying save in 3.2s (one attempt only)`);
        // Общий слот с клиентским throttle: не больше одного ожидающего таймера на endpoint.
        scheduleThrottleRetry(endpoint, 3200, userEmail, true);
      } else {
        console.warn(`⚠️ [${endpoint}] retry also throttled — giving up (debouncedSave should re-fire)`);
      }
      return false;
    }
    // v10.06: Handle unchanged response — server had nothing new to write
    if (json.unchanged) {
      setServerOnline(true);
      rememberDelivered(endpoint, myGen, data);
      dropPendingIfGenAtMost(endpoint, myGen, { data, deleted });
      resyncAfterStaleSave(endpoint, json);
      return true;
    }
    // FIX 9.20: If server returned merged data (with records from other users),
    // sync it back into React state to prevent "disappearing rows"
    if (currentSaveIntent(endpoint) <= myGen
        && json.data && Array.isArray(json.data) && json.data.length > data.length) {
      const setter = conflictSetters[endpoint];
      if (setter) {
        console.log(`🔄 Post-save sync [${endpoint}]: server has ${json.data.length} records (we sent ${data.length}) — syncing`);
        // 2026-07-17 (жюри A M1, tombstone-контракт): сейв-ответ — ЕДИНСТВЕННЫЙ
        // гарантированный канал для сохраняющего браузера (WS-эхо теряется, если сокет
        // не OPEN в момент бродкаста — буферизации в WS-слое нет). Старый голый
        // filterTombstoned тут заново прятал только что восстановленную админом строку.
        // syncServerTombstones делает revive ДО фильтра (правила — src/lib/tombstones.js).
        const filtered = syncServerTombstones(endpoint, {
          data: json.data, tombstoned: json.tombstoned, version: json.version,
        }).data;
        setter(filtered);
        lsSave(endpoint, filtered);
        lastKnownCounts[endpoint] = filtered.length;
      }
    }
    setServerOnline(true);
    rememberDelivered(endpoint, myGen, data);
    dropPendingIfGenAtMost(endpoint, myGen, { data, deleted });
    return true;
  } catch (e) {
    // v9.05 FIX: Don't immediately kill serverOnline — retry first
    // Retry once after 2 seconds
    try {
      if (currentSaveIntent(endpoint) > myGen) {
        queueUserSave(endpoint, { data, userEmail, deleted, gen: myGen, spread: true });
        return false;
      }
      await new Promise(r => setTimeout(r, 2000));
      // Повторная проверка после паузы: B могла встать в очередь за эти 2с.
      if (currentSaveIntent(endpoint) > myGen) {
        queueUserSave(endpoint, { data, userEmail, deleted, gen: myGen, spread: true });
        return false;
      }
      const sentToken = getSessionToken();
      const { res, timing: saveRetryTiming } = await fetchAndTime(`${API_BASE}/${endpoint}`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ data, version: dataVersions[endpoint] || 0, user: userEmail || 'unknown', deleted }),
        signal: AbortSignal.timeout(5000),
      });
      if (res.status === 401) {
        if (!justLoggedIn && getSessionToken() === sentToken) {
          const killedToken = sentToken;
          const killedSession = localStorage.getItem('blitz_session');
          setSessionToken(null);
          localStorage.removeItem('blitz_session');
          setSessionExpiredFlag(true);
          confirmSessionExpired({
            token: killedToken,
            reason: `api-save:${endpoint}`,
            onAlive: () => restoreKilledSession(killedToken, killedSession),
          });
        }
        queueUserSave(endpoint, { data, userEmail, deleted, gen: myGen, spread: true });
        return false;
      }
      if (res.status === 403) {
        if (isIpBanResponse(res)) {
          console.warn(`⛔ apiSave ${endpoint}: доступ с этого адреса закрыт (бан по IP) — правка сохранена локально и остаётся в очереди`);
          setServerOnline(false);
          queueUserSave(endpoint, { data, userEmail, deleted, gen: myGen, spread: true });
          return false;
        }
        console.warn(`⛔ apiSave ${endpoint}: сервер отказал в правах (403) — таблица помечена запрещённой, повторов не будет`);
        forbiddenEndpoints.add(endpoint);
        lsRemove(endpoint);
        purgeDroppedSaves(endpoint);
        pendingSaves.delete(endpoint); // forbidden-403: любое поколение
        return false;
      }
      if (res.ok) {
        const json = await res.json();
        noteHttpResponseSample(res, json, saveRetryTiming);
        if (json.throttled) {
          queueUserSave(endpoint, { data, userEmail, deleted, gen: myGen, spread: false });
          return false;
        }
        if (json.version && json.version !== dataVersions[endpoint]) { // v11.02 L4
          dataVersions[endpoint] = json.version;
          savePersistedVersions(dataVersions);
        }
        setServerOnline(true);
        rememberDelivered(endpoint, myGen, data);
        dropPendingIfGenAtMost(endpoint, myGen, { data, deleted });
        return true;
      }
    } catch (e2) {}
    // Both attempts failed — queue for later retry
    // 2026-08-31 (P5-QUEUE): same fix as the 401 branch above — preserve an already-queued
    // `retries` count instead of silently resetting it via a fresh object.
    // FR1 (fix-round 2026-08-31, жюри A CRITICAL-1): same `deleted`-overwrite bug as the
    // 401 branch — union instead.
    queueUserSave(endpoint, { data, userEmail, deleted, gen: myGen, spread: true });
    console.log(`⚠️ Queued pending save: ${endpoint} (will retry in 30s)`);
    return false;
  } finally {
    endInFlightSave(endpoint);
  }
}

// ── 2026-06-12 per-row saves (crg-deals echo-cascade incident) ──────────────
//
// ЧТО БЫЛО: каждый мутирующий хендлер CRG слал ВЕСЬ массив через apiSave —
// stale-вкладка слала stale-строки, и одна правленная строка (свежий updatedAt)
// легально перетирала более новые данные сервера для ВСЕХ остальных строк её
// 9-часового снимка. Плюс App-уровневый auto-save effect «эхом» отстреливал
// полный массив на применение чужого броадкаста (cameron/zack/office за 7s
// после сейва владельца). Форензика: docs/.history + audit 2026-06-12.
//
// ЧТО ТЕПЕРЬ: apiSaveRows шлёт на сервер ТОЛЬКО изменённые строки (+ explicit
// deletedIds → tombstones). Сервер (backend/routes/crud-post.cjs:386-450)
// мержит по id с row-level LWW по validated updatedAt — частичный массив
// безопасен by design: отсутствующие строки НЕ трогаются.
//
// ПОЧЕМУ НЕ apiSave: вся его клиентская механика заточена под полный массив —
//   - lsSave(endpoint, data) затёр бы full-table LS-кэш частичным массивом;
//   - 20%-drop guard заблокировал бы любой partial save как «data loss»;
//   - throttle-retry перечитывает LS и слал бы этот частичный кэш;
//   - post-save sync (json.data.length > data.length) делал бы blind replace
//     state на каждый row-save.
//
// Механика: per-endpoint queue (Map по id — поздняя правка той же строки
// супersedes раннюю) + коалесcing 50ms + одиночный flight. После КАЖДОГО
// завершённого POST следующий flush ждёт ≥3.3s (серверный per-endpoint
// write-throttle = 3s; см. crud-post.cjs:84) — burst правок batch'ится без
// гарантированного throttled-раунда. Fail → re-queue (не перетирая более
// новые pending-правки тех же id) + retry; после 6 неудач подряд — durable
// rescue в localStorage (persistDroppedSave, тот же контракт что pendingSaves).

const rowSaveQueues = {}; // endpoint -> { rows: Map<id,row>, deleted: Set<id>, timer, inFlight, attempts, userEmail }

/**
 * Сколько правок ждёт отправки в очереди строк (0 = всё ушло на сервер).
 * Нужен восстановлению из аварийного хранилища: запись из него удаляем только когда
 * очередь реально опустела, иначе можно потерять данные второй раз.
 *
 * @param {string} endpoint
 * @returns {number}
 */
export function getRowQueueSize(endpoint) {
  const q = getRowQueue(endpoint);
  return q.rows.size + q.deleted.size;
}

/**
 * Сумма правок во ВСЕХ очередях строк (все endpoint), не только одного.
 * 2026-09-01 (фаза3 П4, свод P9) — нужен предупреждению «войти другим пользователем»
 * (SessionExpiredDialog): pendingSaves.size одной простой очереди недостаточно — CRG и
 * подобные идут отдельным путём (rowSaveQueues), и без этого числа смена пользователя
 * при непустой СТРОЧНОЙ очереди молча теряла бы правки на reload. Read-only, без мутации.
 *
 * @returns {number}
 */
export function getRowQueuesPendingCount() {
  let total = 0;
  for (const q of Object.values(rowSaveQueues)) {
    total += q.rows.size + q.deleted.size;
  }
  return total;
}

function getRowQueue(endpoint) {
  if (!rowSaveQueues[endpoint]) {
    rowSaveQueues[endpoint] = { rows: new Map(), deleted: new Set(), timer: null, inFlight: false, attempts: 0, userEmail: 'unknown' };
  }
  return rowSaveQueues[endpoint];
}

// Test-only helper: сбрасывает очереди между тестами (зеркалит resetSessionTokenCache pattern).
export function _resetRowSaveQueues() {
  for (const k of Object.keys(rowSaveQueues)) {
    const q = rowSaveQueues[k];
    if (q.timer) { clearTimeout(q.timer); }
    delete rowSaveQueues[k];
  }
}

/**
 * apiSaveRows(endpoint, rows, userEmail, deletedIds) — per-row save.
 * rows: ТОЛЬКО изменённые/добавленные строки (каждая с .id).
 * deletedIds: id удалённых строк (сервер ставит tombstone — без этого resurrection).
 * Возвращает true если что-то поставлено в очередь, false если нечего слать.
 */
/**
 * Снять паузу со всех очередей строк. Зовётся при успешном входе: пауза ставится только
 * на время смерти сессии, а вход её отменяет.
 *
 * Почему это отдельная ручка, а не «само рассосётся»: вход в приложении происходит БЕЗ
 * перезагрузки (App.jsx, LoginScreen.onLogin), очередь живёт в памяти той же вкладки —
 * без снятия паузы каждая следующая правка CRG тихо оседала бы в памяти и не уходила на
 * сервер. Ровно та потеря данных, которую этот файл и чинит (находка жюри 27.08).
 *
 * @returns {void}
 */
export function resumeRowQueues() {
  for (const [endpoint, q] of Object.entries(rowSaveQueues)) {
    if (!q || !q.paused) continue;
    q.paused = false;
    if (q.rows.size > 0 || q.deleted.size > 0) scheduleRowFlush(endpoint, 50);
  }
}

export function apiSaveRows(endpoint, rows, userEmail, deletedIds = []) {
  const list = Array.isArray(rows) ? rows.filter(r => r && r.id != null) : [];
  const dels = Array.isArray(deletedIds) ? deletedIds.filter(id => id != null) : [];
  if (list.length === 0 && dels.length === 0) return false;

  // 1. Full-table LS-кэш остаётся когерентным: merge по id + drop deleted.
  //    (НЕ заменяем кэш частичным массивом — это сломало бы apiSave-пути других
  //    страниц, throttle-retry re-read и offline-восстановление.)
  try {
    const cache = lsGet(endpoint, null) || [];
    const byId = new Map();
    for (const r of cache) { if (r && r.id != null) byId.set(r.id, r); }
    for (const r of list) byId.set(r.id, r);
    for (const id of dels) byId.delete(id);
    const nextCache = Array.from(byId.values());
    lsSave(endpoint, nextCache);
    lastKnownCounts[endpoint] = nextCache.length; // baseline для apiSave-guards других путей
  } catch (_) { /* LS недоступен — сетевой путь всё равно работает */ }

  // 2. Queue + coalesce. Map по id: повторная правка той же строки до flush
  //    заменяет payload (шлём только последнюю версию). Delete супersedes edit.
  const q = getRowQueue(endpoint);
  // Страховка к resumeRowQueues: пауза законна только пока сессия признана мёртвой.
  // Если вердикт снят (вход), а ручку почему-то не дёрнули — не держим правки в памяти.
  if (q.paused && !isSessionKnownDead()) q.paused = false;
  // 2026-10-07: у строки CRG маркер `_patchFields` (src/lib/row-patch.js) — сервер берёт
  // только эти поля, поэтому при замене строки в очереди поля прежней правки сохраняются.
  for (const r of list) { q.rows.set(r.id, coalescePatchRows(q.rows.get(r.id), r)); q.deleted.delete(r.id); }
  for (const id of dels) { q.deleted.add(id); q.rows.delete(id); }
  if (userEmail) q.userEmail = userEmail;
  scheduleRowFlush(endpoint, 50); // 50ms — коалесцирует bulk/undo/StrictMode-double-invoke
  return true;
}

function scheduleRowFlush(endpoint, delayMs) {
  const q = getRowQueue(endpoint);
  if (q.timer || q.inFlight) return; // уже запланирован/летит — очередь подхватится
  // 2026-08-27: пауза на время выяснения судьбы сессии и после подтверждённой смерти.
  // Долбить сервер мёртвым токеном бессмысленно, а главное — каждый такой круг раньше
  // сжигал попытку и по шестой выбрасывал правки в аварийное хранилище.
  if (q.paused) return;
  q.timer = setTimeout(() => {
    q.timer = null;
    flushRowQueue(endpoint).catch(() => {});
  }, delayMs);
}

// Exported для тестов (await прямого flush без таймеров) и для возможного
// pagehide-флаша в будущем. В прод-коде вызывается только через scheduleRowFlush.
/**
 * Скопировать всё, что ждёт в очереди строк, в аварийное хранилище — не дожидаясь
 * шести неудачных кругов. Зовётся при ПОДТВЕРЖДЁННОЙ смерти сессии: окно предлагает
 * перезагрузку, а очередь живёт в памяти вкладки и перезагрузку не переживает.
 * Очередь при этом НЕ очищается: если человек войдёт заново без перезагрузки, правки
 * дошлются сами. Повтор безопасен — сервер мержит строки по id.
 *
 * @param {string} endpoint
 * @returns {void}
 */
function rescueQueuedRows(endpoint) {
  const q = getRowQueue(endpoint);
  if (q.rows.size === 0 && q.deleted.size === 0) return;
  persistDroppedSave(endpoint, {
    data: Array.from(q.rows.values()),
    userEmail: q.userEmail,
    deleted: Array.from(q.deleted),
    reason: 'session-expired',
  });
}

// 2026-10-07 (жюри A+B): после успешной доставки строк снять доставленные поля с маркера
// `_patchFields` — у строк в state (conflictSetters), в LS-кэше и у новой правки той же
// строки в очереди. Маркер иначе гаснет только эхом сервера; застрявшее эхо (медленный
// WS) — и следующая правка другого поля пересылала бы доставленное значение поверх
// чужого, более нового. Строка очереди, у которой ничего не осталось, выбрасывается: без
// маркера она ушла бы на сервер ЦЕЛИКОМ.
function pruneDeliveredRows(endpoint, q, delivered) {
  const byId = new Map(delivered.filter((r) => r && r.id != null).map((r) => [r.id, r]));
  if (byId.size === 0) return;
  for (const [id, queued] of q.rows) {
    const d = byId.get(id);
    if (!d) continue;
    const pruned = pruneDeliveredFields(queued, d);
    if (pruned === queued) continue;
    if (Array.isArray(pruned._patchFields)) q.rows.set(id, pruned); else q.rows.delete(id);
  }
  const pruneList = (list) => {
    if (!Array.isArray(list)) return list;
    let changed = false;
    const next = list.map((r) => {
      const d = r && byId.get(r.id);
      const p = d ? pruneDeliveredFields(r, d) : r;
      if (p !== r) changed = true;
      return p;
    });
    return changed ? next : list;
  };
  try {
    const cache = lsGet(endpoint, null);
    const nextCache = pruneList(cache);
    if (nextCache !== cache) lsSave(endpoint, nextCache);
  } catch (_) { /* LS недоступен — state и очередь всё равно чистятся */ }
  const setter = conflictSetters[endpoint];
  if (typeof setter === 'function') setter((prev) => pruneList(prev));
}

export async function flushRowQueue(endpoint) {
  const q = getRowQueue(endpoint);
  if (q.inFlight) return 'busy';
  if (q.rows.size === 0 && q.deleted.size === 0) return 'idle';

  // Snapshot: очередь освобождается СРАЗУ — правки, прилетевшие во время flight,
  // копятся отдельно и уйдут следующим flush'ем.
  const rows = Array.from(q.rows.values());
  const deleted = Array.from(q.deleted);
  q.rows = new Map();
  q.deleted = new Set();
  q.inFlight = true;

  // Re-queue при неудаче: НЕ перетираем более новые pending-правки тех же id,
  // прилетевшие во время flight (они новее snapshot'а).
  const requeue = () => {
    // 2026-10-07: новая правка той же строки уже в очереди — её тело новее, но поля
    // упавшей отправки иначе не доехали бы никогда (сервер берёт только _patchFields).
    for (const r of rows) {
      if (q.deleted.has(r.id)) continue;
      q.rows.set(r.id, q.rows.has(r.id) ? coalescePatchRows(r, q.rows.get(r.id)) : r);
    }
    for (const id of deleted) { q.deleted.add(id); q.rows.delete(id); }
  };
  // Durable rescue после 6 неудач подряд — тот же контракт что flushPendingSaves
  // (persistDroppedSave: localStorage + громкий console.error + CustomEvent).
  // Retry-таймер НЕ ставится здесь: единственная точка планирования — finally
  // ниже (здесь inFlight ещё true, scheduleRowFlush guard всё равно бы скипнул).
  // Jury A M1 (PR #542): серверный 3s write-throttle — ШТАТНАЯ перегрузка, не сбой.
  // Троттл-повторы НЕ сжигают attempts (иначе 6 троттлов за ~20с отправляли бы строки
  // в аварийный стор с громким "Dropped save", хотя retry через 3.3с почти всегда
  // проходит). Бесконечно тоже нельзя (патологический вечный троттл — clock skew и
  // т.п.): после 20 троттлов ПОДРЯД каждый следующий считается сбоем → эскалация в
  // rescue как обычно. Любой успех/не-троттл сбрасывает серию.
  //
  // 2026-09-27 (design v2, W3) — 'kind' заменил булев isThrottle третьим значением:
  //   'throttle' — как раньше, фикс 3.3с, до 20 подряд, attempts не трогает;
  //   'soft'     — сеть/429/408/5xx: тоже НЕ тратит attempts (временный сбой сервера/сети,
  //                не вердикт по данным), но пауза РАСТЁТ 3.3с → ×2 → потолок 30с — вечный
  //                soft-стрик не должен долбить сервер на фиксированном ритме;
  //   'hard'     — прочие отказы (404/409/413/422/…): как раньше, тратит attempts,
  //                эскалирует в rescue на 6-й, и теперь ЗАПОМИНАЕТ httpStatus для rescue.
  const rescueOrRetry = (kind = 'hard') => {
    if (kind === 'throttle') {
      q.throttleStreak = (q.throttleStreak || 0) + 1;
      if (q.throttleStreak <= 20) { requeue(); q.nextDelayMs = 3300; return; } // штатный повтор — attempts не трогаем
    } else if (kind === 'soft') {
      q.throttleStreak = 0;
      q.softFailStreak = (q.softFailStreak || 0) + 1;
      requeue();
      q.nextDelayMs = Math.min(3300 * (2 ** (q.softFailStreak - 1)), 30000);
      return; // attempts не трогаем — временный сбой, не вердикт по данным
    } else {
      q.throttleStreak = 0;
      q.softFailStreak = 0;
    }
    q.attempts += 1;
    requeue();
    q.nextDelayMs = 3300;
    if (q.attempts >= 6) {
      const allRows = Array.from(q.rows.values());
      const allDeleted = Array.from(q.deleted);
      q.rows = new Map();
      q.deleted = new Set();
      q.attempts = 0;
      q.throttleStreak = 0;
      persistDroppedSave(endpoint, { data: allRows, userEmail: q.userEmail, deleted: allDeleted, httpStatus: q.lastStatus });
    }
  };

  // 2026-08-27 (жюри): исход нужен восстановлению из баннера. Пустая очередь НЕ равна
  // «доехало»: на 400 очередь тоже пустеет (повтор не поможет, строки не возвращаются),
  // и по этому признаку баннер отрапортовал бы «Restored» при реальной потере.
  /** @type {'ok'|'throttled'|'rejected'|'session'|'retry'} */
  let outcome = 'retry';

  try {
    const sentToken = getSessionToken();
    const { res, timing: rowTiming } = await fetchAndTime(`${API_BASE}/${endpoint}`, {
      method: 'POST', headers: authHeaders(),
      body: JSON.stringify({ data: rows, version: dataVersions[endpoint] || 0, user: q.userEmail || 'unknown', deleted }),
      signal: AbortSignal.timeout(5000),
    });
    if (res.ok) {
      const json = await res.json().catch(() => ({}));
      noteHttpResponseSample(res, json, rowTiming);
      if (json.version && json.version !== dataVersions[endpoint]) {
        dataVersions[endpoint] = json.version;
        savePersistedVersions(dataVersions);
      }
      setServerOnline(true);
      if (json.throttled) {
        // Сервер пропустил запись (3s per-endpoint write-throttle) — re-queue и
        // повтор после окна. deletes throttle не задевает (crud-post.cjs:84).
        console.log(`🔁 [${endpoint}] row-save throttled — retrying in 3.3s (${rows.length} rows)`);
        rescueOrRetry('throttle'); // throttle ≠ failure (Jury A M1)
        outcome = 'throttled';
      } else {
        q.attempts = 0;
        q.throttleStreak = 0;
        q.softFailStreak = 0; // 2026-09-27 (design v2, W3): успех сбрасывает и растущую soft-паузу
        q.nextDelayMs = 0;
        outcome = 'ok';
        if (json.unchanged) resyncAfterStaleSave(endpoint, json);
        // Отвергнутые как устаревшие — маркер не трогаем: перечитывание выше вернёт строку
        // сервера (она новее), и маркер уйдёт вместе с ней.
        if (!(json.unchanged && Number(json.lwwStale) > 0)) pruneDeliveredRows(endpoint, q, rows);
      }
    } else if (res.status === 400) {
      // Постоянный отказ (валидация) — повтор не поможет, НЕ re-queue.
      const errBody = await res.json().catch(() => ({}));
      console.warn(`⚠️ apiSaveRows ${endpoint} got 400:`, errBody.error || 'unknown');
      q.attempts = 0;
      // Решённый ответ сервера — растущая soft-пауза (W3) кончилась: правки, пришедшие во
      // время полёта, не должны ждать до 30 с (жюри A 27.09).
      q.softFailStreak = 0;
      q.nextDelayMs = 0;
      // 2026-09-27 (design v2, W6): было — молчаливая потеря (rows уже сняты из очереди
      // снимком в начале flushRowQueue, а сюда не попадали НИКУДА). Тот же reason/httpStatus,
      // что у W1/W2b — единый контракт «первый решённый 400 всегда идёт на склад».
      persistDroppedSave(endpoint, { data: rows, userEmail: q.userEmail, deleted, reason: 'rejected', httpStatus: 400 });
      outcome = 'rejected';
    } else if (res.status === 401) {
      // 2026-08-27 (инцидент 25-26.08): раньше 401 падал в общую ветку «повторим позже».
      // Строки целы, сломан ВХОД — повторы бессмысленны, а шесть кругов подряд отправляли
      // правки в аварийное хранилище молча: человек видел «работа пропала» и ни одного
      // сообщения. Теперь: очередь на паузу, спрашиваем сервер, и только по подтверждению
      // кладём правки в durable store — перезагрузка (её же предлагает окно) их не съест.
      // S3b: 401 старого токена — requeue без паузы и без модалки, уйдёт с новым токеном.
      console.warn(`⚠️ apiSaveRows ${endpoint}: сервер отклонил сессию — проверяем`);
      requeue();
      q.attempts = 0;              // отказ входа не должен приближать выброс данных
      if (getSessionToken() === sentToken) {
        q.paused = true;
        confirmSessionExpired({ token: sentToken, reason: `save-rows:${endpoint}` })
          .then((dead) => {
            if (dead || isSessionKnownDead()) {
              rescueQueuedRows(endpoint);   // строки переживут перезагрузку
            } else {
              q.paused = false;             // ложная тревога — продолжаем как обычно
              scheduleRowFlush(endpoint, 1000);
            }
          })
          .catch(() => { q.paused = false; });
        outcome = 'session';
      } else {
        outcome = 'retry';
      }
    } else if (res.status === 403 && isIpBanResponse(res)) {
      // Бан по IP — не вердикт по данным (как в apiSave/flushPendingSaves): строки ждут в
      // очереди, попытки не тратятся. Иначе шесть кругов уносили их на склад, а отчёт о
      // потере под тем же баном получал 403 (жюри A 27.09).
      console.warn(`⛔ apiSaveRows ${endpoint}: бан по IP — строки остаются в очереди`);
      setServerOnline(false);
      rescueOrRetry('soft');
      outcome = 'retry';
    } else if (res.status === 429 || res.status === 408 || res.status >= 500) {
      // 2026-09-27 (design v2, W3): 429/408/5xx — временный сбой сервера, не вердикт по
      // данным (тот же класс, что уже был у 5xx до этой правки) — попытки не тратим,
      // пауза растёт.
      console.warn(`⚠️ apiSaveRows ${endpoint} got ${res.status} — will retry (soft)`);
      rescueOrRetry('soft');
      outcome = 'retry';
    } else {
      // Прочие решённые отказы (404/409/413/422/…) — как раньше: тратит попытку,
      // эскалирует в rescue на 6-й; запоминаем статус для этой rescue-записи.
      console.warn(`⚠️ apiSaveRows ${endpoint} got ${res.status} — will retry`);
      q.lastStatus = res.status;
      rescueOrRetry('hard');
      outcome = 'retry';
    }
  } catch (e) {
    // network failure / timeout — тот же класс, что 429/408/5xx (W3): попытки не тратим.
    rescueOrRetry('soft');
    outcome = 'retry';
  } finally {
    q.inFlight = false;
    if ((q.rows.size > 0 || q.deleted.size > 0) && !q.timer && !q.paused) {
      // Правки прилетели во время flight ЛИБО re-queue выше. Единственная точка
      // планирования retry/next-flush. q.nextDelayMs — растущая soft-пауза (W3) или
      // фиксированные 3.3s throttle/hard-повтора; 3300 — фолбэк для веток без rescueOrRetry
      // (напр. 'ok' с новыми правками, прилетевшими во время flight).
      scheduleRowFlush(endpoint, q.nextDelayMs || 3300);
    }
  }
  return outcome;
}

// -- WebSocket Manager (real-time sync) --
// `export` added (C1): was module-local in App.jsx. Now cross-module — App.jsx imports
// it back for the connect/reconnect effects. Body below is byte-identical to the original.
function retireWebSocket(socket) {
  if (!socket) return;
  socket._blitzRetired = true;
  try { socket.close(); } catch { /* ignore */ }
  if (wsConnection === socket) setWsConnection(null);
  if (_wsSocket === socket) _wsSocket = null;
  if (typeof window !== 'undefined' && window._blitzWS === socket) window._blitzWS = null;
}

export function connectWebSocket() {
  const token = getSessionToken();
  if (!token || isSessionKnownDead()) {
    const sock = wsConnection;
    if (sock && (sock.readyState === WebSocket.OPEN || sock.readyState === WebSocket.CONNECTING)) {
      retireWebSocket(sock);
    }
    return;
  }
  if (wsConnection && (wsConnection.readyState === WebSocket.OPEN || wsConnection.readyState === WebSocket.CONNECTING)) {
    if (wsConnection._blitzAuthToken === token) return; // v9.09: don't double-connect same session
    retireWebSocket(wsConnection);
  }
  try {
    console.log(`🔌 WebSocket connecting to ${WS_URL}...`);
    const socket = new WebSocket(`${WS_URL}?token=${encodeURIComponent(token)}`);
    socket._blitzAuthToken = token;
    setWsConnection(socket);
    window._blitzWS = socket; // FIX 15: expose for clean close before page reload (iOS PWA)
    let closedHandled = false; // v9.09: prevent double-fire from onerror+onclose
    socket.onopen = () => {
      if (socket._blitzRetired || wsConnection !== socket) return;
      console.log('🔌 WebSocket connected ✅');
      _wsSocket = socket;
      setWsReconnectAttempts(0); // reset backoff on success
      setServerOnline(true);
      requestResync();
      // 2026-08-31 (Phase 2 «Офлайн видим», П1): window.__statusFailCount retired —
      // setServerOnline(true) above already calls resetStrikes() internally, this call
      // is the explicit belt-and-suspenders the brief calls for at the WS-onopen site
      // specifically (the strikes/offlineConfirmed debounce that replaced the old
      // window global — see connection-state.js).
      resetStrikes();
      // Delay flush by 2s after reconnect — stabilize connection before sending pending saves
      // Prevents the Android IP-switch save storm (multiple reconnects firing rapid saves)
      setTimeout(flushPendingSaves, 2000);
      ensurePresenceTracker().flush();
    };
    socket.onmessage = (event) => {
      if (socket._blitzRetired || wsConnection !== socket) return;
      try {
        const msg = JSON.parse(event.data);
        if (msg.type === 'update') {
          if (Number.isFinite(Number(msg.timestamp))) noteServerLowerBound(Number(msg.timestamp)); // WS lower bound only
          if (msg.version) dataVersions[msg.table] = msg.version;
          // v10.06 → 2026-07-17 (tombstone-контракт): обработка msg.deleted/msg.tombstoned
          // и фильтрация msg.data сведены в syncServerTombstones. Дополнительно — reconcile:
          // canary-бродкаст (gen-balances/selling-offers) теперь несёт массив tombstoned
          // ВСЕГДА (даже пустой), и строка, присутствующая в msg.data без своего id в
          // msg.tombstoned/msg.deleted, оживляется (revive после admin-restore) ДО
          // фильтра — иначе filterTombstoned не пропустил бы её к листенерам страниц.
          // PR R (sub_R3, 28.09): та же логика для daily-calcs-data строк, ставших
          // delete-protected — снимает стале-метки старой вкладки ДО syncServerTombstones.
          if (msg.table === 'daily-calcs-data' && Array.isArray(msg.data)) releaseProtectedDcMarks(msg.data);
          const synced = syncServerTombstones(msg.table, {
            data: msg.data, tombstoned: msg.tombstoned, deleted: msg.deleted, version: msg.version,
          });
          if (Array.isArray(synced.data)) {
            lsSave(msg.table, synced.data);
            // Pass filtered data to listeners
            wsListeners.forEach(fn => fn({ ...msg, data: synced.data }));
          } else {
            wsListeners.forEach(fn => fn(msg));
          }
        } else if (msg.type === 'versions') {
          Object.entries(msg.versions).forEach(([k, v]) => { dataVersions[k] = v; });
        } else if (msg.type === 'presence-count') { notifyPresence(msg.count, msg.users);
        } else if (msg.type === 'heartbeat') {
          if (Number.isFinite(Number(msg.timestamp))) noteServerLowerBound(Number(msg.timestamp)); // WS lower bound only
          // Phase 1a: reload is triggered ONLY by build-hash mismatch.
          // VERSION mismatch is logged for diagnostics but does NOT trigger reload —
          // build-hash is the authoritative signal (covers both bumped-version deploys
          // and structural deploys where version wasn't bumped). This eliminates the
          // "4 places" VERSION sync burden: only package.json needs to change.
          const versionMismatch = msg.v && msg.v !== VERSION; // log only, not reload trigger
          const buildMismatch = shouldReloadOnHeartbeat(msg, { version: VERSION, buildHash: INITIAL_BUILD_HASH });
          if (versionMismatch && !buildMismatch) {
            // Version label differs but build hash matches — same bundle, just a label change.
            // Log only; user does not get interrupted.
            console.info(`ℹ️ Server label v${msg.v} ≠ client label v${VERSION} (same build hash — no reload)`);
          }
          if (buildMismatch && !window.__versionReloadScheduled) {
            console.warn(`⚠️ Server build ${msg.bh} ≠ Client build ${INITIAL_BUILD_HASH} — reloading in 5s`);
            window.__versionReloadScheduled = true;
            // v31.99 Bug #66: defer reload если user активно набирает в input/textarea
            // или contenteditable. Без этого mobile user может потерять 10s of typing.
            // Check каждые 1.5s; reload только когда no input focused. Cap retries 60
            // (~90s max wait) — после этого force reload regardless (deploy не может
            // ждать вечность).
            let _reloadAttempts = 0;
            const _tryReload = () => {
              const ae = document.activeElement;
              const isTyping = ae && (
                ae.tagName === 'INPUT' || ae.tagName === 'TEXTAREA' || ae.isContentEditable
              );
              if (!isTyping || _reloadAttempts >= 60) {
                window.location.reload();
              } else {
                _reloadAttempts++;
                setTimeout(_tryReload, 1500);
              }
            };
            setTimeout(_tryReload, 5000);
          }
        }
      } catch {}
    };
    const scheduleReconnect = (code) => {
      if (!getSessionToken() || isSessionKnownDead()) return;
      const delay = Math.min(1000 * Math.pow(2, Math.min(wsReconnectAttempts, 10)), 15000);
      setWsReconnectAttempts(wsReconnectAttempts + 1);
      console.log(`🔌 WS closed (${code}). Reconnect #${wsReconnectAttempts} in ${delay/1000}s`);
      if (!wsReconnectTimer) setWsReconnectTimer(setTimeout(() => { setWsReconnectTimer(null); connectWebSocket(); }, delay));
    };
    socket.onclose = (e) => {
      if (closedHandled) return;
      closedHandled = true;
      if (socket._blitzRetired) {
        if (wsConnection === socket) setWsConnection(null);
        return;
      }
      const stillMine = wsConnection === socket;
      if (stillMine) setWsConnection(null);
      // Code 4001 = auth expired — stop retrying, session is dead
      if (e.code === 4001) {
        console.warn('🔌 WS auth failed (4001) — session expired, not retrying');
        // 2026-08-18: 4001 сервер шлёт и на выход по кнопке ('Logged out'). Там токен
        // уже стёрт clearSession'ом — по нему и отличаем чужую смерть сессии от своей.
        // Тут ничего не стёрто, поэтому и возвращать нечего — только подтвердить.
        if (getSessionToken() === token) {
          confirmSessionExpired({ token, reason: 'ws-4001' });
        }
        return;
      }
      if (!stillMine) return;
      scheduleReconnect(e.code);
    };
    socket.onerror = () => {
      if (closedHandled) return; // onclose will handle reconnect
      closedHandled = true;
      if (socket._blitzRetired) {
        if (wsConnection === socket) setWsConnection(null);
        return;
      }
      const stillMine = wsConnection === socket;
      if (stillMine) setWsConnection(null);
      if (!stillMine) return;
      scheduleReconnect('error');
    };
  } catch (e) {
    console.warn('🔌 WebSocket creation failed:', e.message, '— using HTTP polling');
  }
}

export function onWsUpdate(fn) { wsListeners.add(fn); return () => wsListeners.delete(fn); }

// ── Presence API ─────────────────────────────────────────────────────────────

// Кэш последнего presence-payload: новый подписчик (напр. свежесмонтированная шапка
// после переключения вкладки) сразу получает актуальное значение, не дожидаясь
// следующего broadcast от сервера — иначе счётчик «пропадал» при навигации.
let _lastPresence = null;

export function onPresenceUpdate(fn) {
  wsPresenceListeners.add(fn);
  if (_lastPresence) { try { fn(_lastPresence); } catch {} }
  return () => wsPresenceListeners.delete(fn);
}

export function notifyPresence(count, users) {
  const payload = { count: count || 0, users: Array.isArray(users) ? users : [] };
  _lastPresence = payload;
  wsPresenceListeners.forEach(fn => { try { fn(payload); } catch {} });
}

// Module-level socket reference for sendPresence (set in onopen).
let _wsSocket = null;

export function sendPresence(active) {
  try {
    if (_wsSocket && _wsSocket.readyState === 1) {
      _wsSocket.send(JSON.stringify({ type: 'presence', active: !!active }));
    }
  } catch {}
}

let _presenceTracker = null;
export function ensurePresenceTracker() {
  if (_presenceTracker) return _presenceTracker;
  _presenceTracker = createPresenceTracker({ send: sendPresence });
  _presenceTracker.start();
  return _presenceTracker;
}

